import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { isNetworkError } from "../../../utils/networkErrorHandler";
import { convertMessagesTimestamps } from "../../../utils/timestampUtils";
import { setInboxCounts, removeContactFromAssignment, addContactToAssignment } from "./inboxSettingsSlice";
import { clearSelectedContactID } from "../../../redux/Slice/Inbox/inboxAllSlice";
import { markMessageAsRead } from "./InboxMessageSlice";
import { getAuthToken } from "../../../utils/getAuthToken";
import { getCached, setCached, invalidate, STORES, TTL } from "../../../utils/indexedDBCache";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Utility function to ensure timestamps are serializable
const ensureSerializableTimestamp = (timestamp) => {
  if (!timestamp) return new Date().toISOString();
  if (timestamp instanceof Date) return timestamp.toISOString();
  if (typeof timestamp === 'number') return new Date(timestamp).toISOString();
  if (typeof timestamp === 'string') return timestamp; // Assume it's already ISO string
  return new Date().toISOString();
};

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 5000; // 5 seconds - much more aggressive

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);
  
  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }
  
  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetInboxRecentCaches = () => {
  debounceMap.clear();
  activeConnectionFilter = null; // multi-number filter belongs to the previous session
};

// Shared GET /api/inbox/getinbox call used by fetchInbox, loadMoreInbox,
// fetchClosedTabInbox, searchInbox and refreshInboxBackground — one place for
// the auth-token lookup + axios call instead of five near-identical copies.
// Throws the raw axios/network error (or a `{ isNoToken: true }` error when no
// auth token is available) so each caller can keep its own error-message
// classification, which genuinely differs between call sites.
// Multi-number (2026-10): the WhatsApp number the Inbox is currently filtered to (null = "All
// numbers", the default and every single-number workspace). Held at module level, like debounceMap,
// so every list request below -- first page, load-more, tab fetches, search, background refresh --
// applies the same filter without each call site having to pass it. Set only by setConnectionFilter.
let activeConnectionFilter = null;

async function requestInboxList(params, { signal } = {}) {
  const token = await getAuthToken();
  if (!token) {
    const err = new Error("NO_TOKEN");
    err.isNoToken = true;
    throw err;
  }
  const response = await axios.get(
    `${SERVER_DOMAIN}/api/inbox/getinbox`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: activeConnectionFilter ? { ...params, connectionId: activeConnectionFilter } : params,
      signal,
    }
  );
  return response.data;
}

// Async thunk for fetching inbox data (skips Closed on initial load for performance)
export const fetchInbox = createAsyncThunk(
  "inbox/fetchInbox",
  async (_, { rejectWithValue, dispatch }) => {
    // Debounce check
    if (shouldDebounce('fetchInbox')) {
      return rejectWithValue("Debounced");
    }

    // Stale-while-revalidate: serve cached data immediately, refresh in background.
    // Component renders with cached data in ~5ms; fresh data arrives silently after network completes.
    // The cached snapshot is the unfiltered inbox, so it is neither read nor written while a number
    // filter is active.
    const cached = activeConnectionFilter ? null : await getCached(STORES.INBOX_LISTS, 'snapshot');
    if (cached) {
      dispatch(refreshInboxBackground());
      return cached;
    }

    try {
      const data = await requestInboxList({ page: 1, limit: 50, skipClosed: true });
      // Fire-and-forget — do not await, never block the thunk return.
      if (!activeConnectionFilter) setCached(STORES.INBOX_LISTS, 'snapshot', data, TTL.INBOX_LISTS);
      return data;
    } catch (error) {
      if (error.isNoToken) {
        return rejectWithValue("No token found");
      }

      // Check if it's a network error using the utility function
      if (isNetworkError(error)) {
        console.log('🚫 Network error detected, skipping error display');
        return rejectWithValue("Debounced"); // This will be ignored by the reducer
      }

      // Handle different types of errors appropriately
      let errorMessage;
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        errorMessage = "Authentication required. Please log in again.";
      } else {
        // For other errors, extract the message safely
        errorMessage = error.response?.data?.message ||
                      error.response?.data?.error ||
                      error.message ||
                      "Failed to fetch inbox data";
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for deleting a chat conversation (soft delete)
export const deleteChat = createAsyncThunk(
  "inbox/deleteChat",
  async (contactId, { rejectWithValue, dispatch, getState }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.delete(
        `${SERVER_DOMAIN}/api/messages/delete-chat/${contactId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Check if the deleted contact is currently selected
      const state = getState();
      const selectedContactID = state?.inboxAll?.selectedContactID;
      if (selectedContactID && String(selectedContactID) === String(contactId)) {
        // Clear the selected contact if it's the one being deleted
        dispatch(clearSelectedContactID());
      }

      // Refresh inbox data after deletion
      dispatch(fetchInbox());

      return { contactId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to delete chat";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for deleting a contact (soft delete - deletes both contact and chats)
export const deleteContact = createAsyncThunk(
  "inbox/deleteContact",
  async (contactId, { rejectWithValue, dispatch, getState }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.delete(
        `${SERVER_DOMAIN}/api/contacts/delete/${contactId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Check if the deleted contact is currently selected
      const state = getState();
      const selectedContactID = state?.inboxAll?.selectedContactID;
      if (selectedContactID && String(selectedContactID) === String(contactId)) {
        // Clear the selected contact if it's the one being deleted
        dispatch(clearSelectedContactID());
      }

      // Refresh inbox data after deletion
      dispatch(fetchInbox());

      return { contactId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to delete contact";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for pinning/unpinning a contact
// Group analogue of deleteChat/togglePinContact below (WhatsApp Groups plan, Phase G7
// follow-up) -- ContactsModel/MessageModel-specific endpoints would 404 for a group id, so
// these hit the dedicated whatsapp-groups routes instead. Reuses the exact same fulfilled
// shape ({ contactId, ...response.data }) so the existing reducer cases for deleteChat/
// togglePinContact below can be reused unchanged for these too (see extraReducers).
export const deleteGroupChat = createAsyncThunk(
  "inbox/deleteGroupChat",
  async (groupId, { rejectWithValue, dispatch, getState }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.delete(`${SERVER_DOMAIN}/api/whatsapp-groups/${groupId}/chat`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const state = getState();
      const selectedContactID = state?.inboxAll?.selectedContactID;
      if (selectedContactID && String(selectedContactID) === String(groupId)) {
        dispatch(clearSelectedContactID());
      }
      dispatch(fetchInbox());
      return { contactId: groupId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to delete group chat";
      return rejectWithValue(errorMessage);
    }
  }
);

// Persists the "read" state for a group's currently-unread inbound messages (bug fix,
// 2026-09-24 live testing: the Inbox card's unread badge kept growing/reappearing because
// nothing ever transitioned a message's stored status away from "received"/"delivered" --
// clearUnreadCount below only ever zeroed the count in local Redux state, which the next
// recompute silently overwrote with the real, never-decreasing DB total). Fire-and-forget,
// no fetchInbox() dispatch here -- clearUnreadCount (called alongside this in
// useContactSelection.js's selectGroup) already gives the immediate local UI update; this
// call just makes sure that state stays true on the next recompute instead of reverting.
export const markGroupAsRead = createAsyncThunk(
  "inbox/markGroupAsRead",
  async (groupId, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.put(`${SERVER_DOMAIN}/api/whatsapp-groups/${groupId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { contactId: groupId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to mark group as read";
      return rejectWithValue(errorMessage);
    }
  }
);

export const toggleGroupPin = createAsyncThunk(
  "inbox/toggleGroupPin",
  async (groupId, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");
    try {
      const response = await axios.put(`${SERVER_DOMAIN}/api/whatsapp-groups/${groupId}/pin`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      dispatch(fetchInbox());
      return { contactId: groupId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to toggle pin status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const togglePinContact = createAsyncThunk(
  "inbox/togglePinContact",
  async (contactId, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.put(
        `${SERVER_DOMAIN}/api/contacts/pin/${contactId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Refresh inbox data after pinning/unpinning
      dispatch(fetchInbox());

      return { contactId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to toggle pin status";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for blocking a contact (WhatsApp/Instagram/Facebook)
export const blockContact = createAsyncThunk(
  "inbox/blockContact",
  async (contactId, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.put(
        `${SERVER_DOMAIN}/api/contacts/block/${contactId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Invalidate the stale-while-revalidate snapshot BEFORE refreshing — otherwise
      // fetchInbox() below can serve back the cached pre-block snapshot (still showing
      // the old Blocked count) and stomp the real-time count this action already applied.
      await invalidate(STORES.INBOX_LISTS, 'snapshot');
      // Refresh inbox data after blocking
      dispatch(fetchInbox());

      return { contactId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          "Failed to block contact";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for unblocking a contact
export const unblockContact = createAsyncThunk(
  "inbox/unblockContact",
  async (contactId, { rejectWithValue, dispatch }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.put(
        `${SERVER_DOMAIN}/api/contacts/unblock/${contactId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Invalidate the stale-while-revalidate snapshot BEFORE refreshing — otherwise
      // fetchInbox() below can serve back the cached pre-unblock snapshot (still showing
      // the old Blocked count) and stomp the real-time count this action already applied.
      await invalidate(STORES.INBOX_LISTS, 'snapshot');
      // Refresh inbox data after unblocking
      dispatch(fetchInbox());

      return { contactId, ...response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          "Failed to unblock contact";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to load the next page for a specific inbox category
export const loadMoreInbox = createAsyncThunk(
  "inbox/loadMoreInbox",
  async (category, { rejectWithValue, getState }) => {
    const state = getState();
    // Lifecycle tabs (`lifecycle:${stageId}`) paginate against their own per-stage cache
    // (lifecycleByStage) instead of the shared pagination object every other category
    // uses — see fetchLifecycleTabInbox for why Lifecycle needs its own bucket per stage.
    const isLifecycle = typeof category === "string" && category.startsWith("lifecycle:");
    const lifecycleStageId = isLifecycle ? category.slice("lifecycle:".length) : null;
    // AI agent tabs (`agent:${agentId}`) paginate against their own per-agent cache
    // (agentByAgentId), same reasoning as Lifecycle just above — see fetchAgentTabInbox.
    const isAgent = typeof category === "string" && category.startsWith("agent:");
    const agentId = isAgent ? category.slice("agent:".length) : null;

    const pagination = isLifecycle
      ? state.inboxRecent?.lifecycleByStage?.[lifecycleStageId]
      : isAgent
      ? state.inboxRecent?.agentByAgentId?.[agentId]
      : state.inboxRecent?.pagination?.[category];
    if (!pagination?.hasMore) return rejectWithValue("No more data");

    const nextPage = (pagination.page || 1) + 1;
    const limit    = pagination.limit || 50;

    try {
      // Blocked contacts are always excluded from the general multi-category response
      // (see inboxController.js BLOCKED CONTACTS block) — unlike Successful/Unsuccessful,
      // which still come back fully hydrated here as long as skipClosed isn't set. So
      // 'Blocked' needs the onlyCategory fast path explicitly to actually get more rows.
      const params = { page: nextPage, limit };
      if (category === 'Blocked') params.category = 'Blocked';
      if (category === 'Groups') params.category = 'Groups';
      if (isLifecycle) {
        params.category = 'Lifecycle';
        params.lifecycleStageId = lifecycleStageId;
      }
      if (isAgent) {
        params.category = 'Agent';
        params.agentId = agentId;
      }
      const data = await requestInboxList(params);
      return { payload: data, category, lifecycleStageId, agentId, page: nextPage, limit };
    } catch (error) {
      if (error.isNoToken) return rejectWithValue("No token found");
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for lazily loading the Closed tab on first click.
// `category` is always 'Closed' — the backend (see inboxController.js
// onlyCategory fast path) then only recomputes/hydrates that one status instead of
// re-fetching All/You/Active/Unassigned/TeamMembers/Teams all over again.
export const fetchClosedTabInbox = createAsyncThunk(
  "inbox/fetchClosedTabInbox",
  async (category, { rejectWithValue, getState }) => {
    if (category !== 'Closed'&& category !== 'Blocked' && category !== 'Groups') {
      return rejectWithValue("Invalid category");
    }
    const state = getState();
    if (state.inboxRecent?.lazyTabsLoaded?.[category]) {
      return rejectWithValue("Already loaded");
    }
    try {
      return await requestInboxList({ page: 1, limit: 50, category });
    } catch (error) {
      if (error.isNoToken) return rejectWithValue("No token found");
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetches the contacts currently in one Lifecycle stage (agent clicked a stage
// in InboxSettings.jsx's Lifecycle rail, or a socket event needs to refresh a
// live-viewed stage), reusing the exact same onlyCategory fast path/hydration as
// fetchClosedTabInbox above — see inboxController.js's 'Lifecycle' branch.
//
// Session-cached per stage id, mirroring Closed/Blocked's "fetch once, then
// reuse" pattern (see lifecycleByStage in initialState) — unlike Closed/Blocked's
// single shared flag, there can be many stages, so each gets its own cache
// entry, checked/skipped right here (not in the caller) so every call site gets
// the same guarantee for free. Pass `force: true` to bypass the cache — used by
// socketHandler.js when a stage that's ALREADY cached needs a genuine refresh
// (a contact just moved into it while it's the one currently being viewed).
export const fetchLifecycleTabInbox = createAsyncThunk(
  "inbox/fetchLifecycleTabInbox",
  async (arg, { rejectWithValue, getState }) => {
    const lifecycleStageId = typeof arg === "string" ? arg : arg?.stageId;
    const force = typeof arg === "object" && arg?.force === true;
    if (!lifecycleStageId) return rejectWithValue("No stage id");
    if (!force && getState().inboxRecent?.lifecycleByStage?.[lifecycleStageId]) {
      // Already cached this session — same "skip, not an error" rejection shape
      // as fetchClosedTabInbox's "Already loaded" above.
      return rejectWithValue("Already cached");
    }
    try {
      return await requestInboxList({ page: 1, limit: 50, category: "Lifecycle", lifecycleStageId });
    } catch (error) {
      if (error.isNoToken) return rejectWithValue("No token found");
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetches one AI agent's row in the Inbox rail — same on-demand, session-cached
// pattern as fetchLifecycleTabInbox just above (see inboxController.js's 'Agent'
// onlyCategory branch, narrowed by aiTakeover.agentId instead of lifecycleStageId).
export const fetchAgentTabInbox = createAsyncThunk(
  "inbox/fetchAgentTabInbox",
  async (arg, { rejectWithValue, getState }) => {
    const agentId = typeof arg === "string" ? arg : arg?.agentId;
    const force = typeof arg === "object" && arg?.force === true;
    if (!agentId) return rejectWithValue("No agent id");
    if (!force && getState().inboxRecent?.agentByAgentId?.[agentId]) {
      // Already cached this session — same "skip, not an error" rejection shape
      // as fetchLifecycleTabInbox's "Already cached" above.
      return rejectWithValue("Already cached");
    }
    try {
      return await requestInboxList({ page: 1, limit: 50, category: "Agent", agentId });
    } catch (error) {
      if (error.isNoToken) return rejectWithValue("No token found");
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// In-flight search request controller — aborted whenever a newer search fires,
// so a slow stale response can never race ahead of / overwrite newer results.
let searchAbortController = null;

// Search across the full server dataset (name / phone)
export const searchInbox = createAsyncThunk(
  "inbox/searchInbox",
  async (arg, { rejectWithValue }) => {
    // arg is the term, or { term, category: "Groups" } to search groups by name (Groups tab); the Chats tabs search contacts only.
    const searchTerm = typeof arg === "string" ? arg : arg?.term;
    const category = typeof arg === "object" && arg?.category === "Groups" ? "Groups" : null;
    if (!searchTerm?.trim()) return rejectWithValue("Empty");

    if (searchAbortController) searchAbortController.abort();
    const controller = new AbortController();
    searchAbortController = controller;

    try {
      return await requestInboxList(
        { page: 1, limit: 100, search: searchTerm.trim(), ...(category ? { category } : {}) },
        { signal: controller.signal }
      );
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return rejectWithValue("Cancelled");
      }
      if (error.isNoToken) return rejectWithValue("No token found");
      return rejectWithValue(error.response?.data?.message || error.message);
    } finally {
      if (searchAbortController === controller) searchAbortController = null;
    }
  }
);

const MAX_CONTACTS_PER_CATEGORY = 300;

// Shared transform applied by both the blocking fetch and the background refresh.
// Called inside Immer's produce callback so direct state mutations are tracked.
const applyInboxPayload = (state, payload) => {
  if (payload && typeof payload === 'object') {
    const transformed = {};
    const counts = {};
    Object.keys(payload).forEach(key => {
      if (payload[key] && Array.isArray(payload[key].data)) {
        // skipClosed leaves Closed data empty with hasMore:true as a
        // "not loaded yet" sentinel (see inboxController.js getInbox). Don't let that
        // placeholder stomp real data fetchClosedTabInbox already loaded into state —
        // only the count (always accurate) should update from this payload.
        const isWithheldClosedCategory =
          ( key === 'Closed' || key === 'Blocked' || key === 'Groups') &&
          payload[key].data.length === 0 &&
          payload[key].hasMore === true;
        if (isWithheldClosedCategory) {
          counts[key] = payload[key].count ?? state.counts[key];
          // Bug fix (2026-09-12): this branch used to return without touching
          // state.pagination[key], leaving hasMore stuck at its previous/default
          // value (false on a fresh session). That let selectInboxRecentCounts's
          // derive() ignore the real server count the instant a single contact
          // got closed live (pushed straight into the local array by a separate
          // socket handler) — local length (1) beat the real total because
          // hasMore was wrongly false. Setting hasMore:true + totalCount here,
          // same shape as the non-withheld branch below, is what tells derive()
          // to trust the server's real count over a small local array.
          if (state.pagination && state.pagination[key] !== undefined) {
            state.pagination[key] = {
              page:       payload[key].page  ?? 1,
              limit:      payload[key].limit ?? 50,
              hasMore:    true,
              totalCount: payload[key].count ?? state.counts[key],
              baseLen:    0,
            };
          }
          return;
        }
        const convertedData = convertMessagesTimestamps(payload[key].data);
        transformed[key] = convertedData;
        counts[key] = payload[key].count ?? convertedData.length;
        if (state.pagination && state.pagination[key] !== undefined) {
          state.pagination[key] = {
            page:       payload[key].page    ?? 1,
            limit:      payload[key].limit   ?? 50,
            hasMore:    payload[key].hasMore ?? false,
            totalCount: payload[key].count   ?? convertedData.length,
            baseLen:    convertedData.length,
          };
        }
      } else if (Array.isArray(payload[key])) {
        transformed[key] = payload[key].map(item => ({
          ...item,
          data: Array.isArray(item.data) ? convertMessagesTimestamps(item.data) : [],
          count: item.count || (Array.isArray(item.data) ? item.data.length : 0),
        }));
        counts[key] = transformed[key].reduce(
          (sum, item) => sum + (item.count || (Array.isArray(item.data) ? item.data.length : 0)), 0
        );
      } else {
        transformed[key] = [];
        counts[key] = 0;
      }
    });
    ['All', 'You', 'Unassigned', 'Assigned', 'Active', 'Closed','Blocked', 'Groups'].forEach(cat => {
      if (transformed[cat] && transformed[cat].length > MAX_CONTACTS_PER_CATEGORY) {
        transformed[cat] = transformed[cat].slice(0, MAX_CONTACTS_PER_CATEGORY);
      }
    });
    // Merge into existing state rather than replacing — this preserves categories absent
    // from the response (e.g. Closed when skipClosed:true is used by
    // the background refresh), so they are not wiped from state on every refresh cycle.
    Object.keys(transformed).forEach(key => {
      state.data[key] = transformed[key];
    });
    Object.keys(counts).forEach(key => {
      state.counts[key] = counts[key];
    });
  } else {
    const counts = {};
    Object.keys(payload || {}).forEach(key => {
      state.data[key] = Array.isArray(payload[key]) ? payload[key] : [];
      counts[key] = Array.isArray(payload[key]) ? payload[key].length : 0;
    });
    Object.keys(counts).forEach(key => {
      state.counts[key] = counts[key];
    });
  }
  if (typeof window !== 'undefined' && window.dispatchRedux) {
    window.calculateAndDispatchInboxCounts && window.calculateAndDispatchInboxCounts(window.dispatchRedux, state.data);
  }
};

// Prevents two concurrent background refreshes from racing each other.
let backgroundRefreshInFlight = false;

// Silent background refresh — no loading spinner, updates state when complete.
// Only dispatched when fetchInbox returns stale cached data (stale-while-revalidate).
const refreshInboxBackground = createAsyncThunk(
  "inbox/refreshInboxBackground",
  async (_, { rejectWithValue }) => {
    if (backgroundRefreshInFlight) return rejectWithValue("AlreadyRefreshing");
    backgroundRefreshInFlight = true;
    try {
      const data = await requestInboxList({ page: 1, limit: 50, skipClosed: true });
      if (!activeConnectionFilter) setCached(STORES.INBOX_LISTS, 'snapshot', data, TTL.INBOX_LISTS);
      return data;
    } catch (error) {
      if (error.isNoToken) return rejectWithValue("No token");
      if (isNetworkError(error)) return rejectWithValue("NetworkError");
      return rejectWithValue(error.response?.data?.message || error.message);
    } finally {
      backgroundRefreshInFlight = false;
    }
  }
);

// Multi-number (2026-10): switch the Inbox to one WhatsApp number (or back to all with null).
// Always a fresh fetch that bypasses the cache and the debounce -- a filtered list is never served
// from the unfiltered snapshot. The lazily-loaded tabs (Closed/Blocked/Groups/Lifecycle/Agent) are
// cleared in the reducer below so they refetch under the new filter when next opened.
// Is this chat already in one of the inbox lists that are loaded (All, Me, Active, ... and the team / team-member buckets)?
// Used by addMessageCard's number filter to tell an update to a card you can see from a chat it cannot vouch for.
const cardIdOf = (c) => String(c?.contactId ?? c?.contactID ?? c?._id ?? "");
const isContactInLoadedLists = (data, contactIdStr) => {
  if (!data || typeof data !== "object" || !contactIdStr) return false;
  for (const value of Object.values(data)) {
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (cardIdOf(item) === contactIdStr) return true;
      if (item && Array.isArray(item.data) && item.data.some((c) => cardIdOf(c) === contactIdStr)) return true; // team / member buckets
    }
  }
  return false;
};

// The first loaded card for a contact (any list, including the team / member buckets), or null.
const findLoadedCard = (data, contactIdStr) => {
  if (!data || typeof data !== "object" || !contactIdStr) return null;
  for (const value of Object.values(data)) {
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (cardIdOf(item) === contactIdStr) return item;
      if (item && Array.isArray(item.data)) {
        const hit = item.data.find((c) => cardIdOf(c) === contactIdStr);
        if (hit) return hit;
      }
    }
  }
  return null;
};

// Hand-built live cards (read update, assignment, close, block, label) carry no platform, so a card they CREATE would default to
// "whatsapp" -- no number badge and a misleading 24-hour clock on a QR chat. Fill in what is provably known, never overwrite:
//   - a card that carries a QR number is a QR card (Meta / Instagram / Facebook / website chats never have a number);
//   - an event with no platform (or the "whatsapp" fallback) for a chat already loaded as QR keeps that chat's platform and number.
// Returns the same object when nothing needs filling.
const withQrIdentity = (data, contactIdStr, contactData) => {
  const platform = contactData.platform;
  if (platform && platform !== "whatsapp") return contactData;
  if (contactData.connectionId) return { ...contactData, platform: "qr" };
  const known = findLoadedCard(data, contactIdStr);
  if (known && known.platform === "qr") {
    return { ...contactData, platform: "qr", ...(known.connectionId ? { connectionId: known.connectionId } : {}) };
  }
  return contactData;
};

export const setConnectionFilter = createAsyncThunk(
  "inbox/setConnectionFilter",
  async (connectionId, { rejectWithValue }) => {
    activeConnectionFilter = connectionId || null;
    try {
      const data = await requestInboxList({ page: 1, limit: 50, skipClosed: true });
      return { connectionId: activeConnectionFilter, data };
    } catch (error) {
      if (error.isNoToken) return rejectWithValue("No token found");
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to filter inbox");
    }
  }
);

// Slice definition
const inboxRecentSlice = createSlice({
  name: "inbox",
  initialState: {
    data: {
      All: [],
      You: [],
      Active: [],
      Unassigned: [],
      Assigned: [],
      Closed: [],
      Blocked: [],
      Groups: [],
    },
    counts: {
      All: 0,
      You: 0,
      Active: 0,
      Unassigned: 0,
      Assigned: 0,
      Closed: 0,
      Blocked: 0,
      Groups: 0,
    },
    pagination: {
      All:          { page: 1, limit: 50, hasMore: false, totalCount: 0 },
      You:          { page: 1, limit: 50, hasMore: false, totalCount: 0 },
      Active:       { page: 1, limit: 50, hasMore: false, totalCount: 0 },
      Unassigned:   { page: 1, limit: 50, hasMore: false, totalCount: 0 },
      Assigned:     { page: 1, limit: 50, hasMore: false, totalCount: 0 },
      Closed:       { page: 1, limit: 50, hasMore: false, totalCount: 0 },
      Groups:       { page: 1, limit: 50, hasMore: false, totalCount: 0 },
    },
    // Per-stage session cache — { [stageId]: { data, count, page, limit, hasMore, loadedAt } }.
    // Keyed by stage id (unlike Closed/Blocked's single fixed bucket) since there can be many
    // stages, each independently cached once visited. Kept live in the background by
    // socketHandler.js's handleContactLifecycleUpdated for EVERY cached stage, not just the
    // one currently on screen — a contact leaving/entering a cached-but-not-viewed stage still
    // patches/invalidates its entry, so revisiting it later never shows silently stale data.
    lifecycleByStage: {},
    // Multi-number: the WhatsApp number the list is filtered to; null = "All numbers".
    selectedConnectionId: null,
    // Which stage is the one currently rendered — lets the UI tell "stage clicked but still
    // loading" apart from "stage has zero contacts".
    selectedLifecycleStageId: null,
    lifecycleTabLoading: false,
    // Same per-id session cache as lifecycleByStage above, but for an AI agent's row in
    // the Inbox rail — { [agentId]: { data, count, page, limit, hasMore, loadedAt } }.
    // See fetchAgentTabInbox and inboxController.js's 'Agent' onlyCategory branch.
    agentByAgentId: {},
    selectedAgentId: null,
    agentTabLoading: false,
    lazyTabsLoaded: {
      Closed: false,
      // Was accidentally overwritten to a pagination-shaped object by an old merge
      // (commit 8a330960, "dashboard") -- being truthy, it permanently satisfied every
      // `!lazyTabsLoaded.Blocked` guard (InboxSettings.jsx's tab click, updateAllCounts)
      // from app boot, so the real fetchClosedTabInbox("Blocked") fetch never fired.
      // The tab only ever showed contacts that arrived via a live block/unblock socket
      // event during the current session, never the actual historical list, and the
      // Blocked count badge could go stale/wrong once one of those live events fired
      // (state.counts.Blocked got recomputed off the incomplete local array). Restored
      // to a plain boolean, matching Closed above and every read site's usage
      // (2026-09-08, live-audited in chrome).
      Blocked: false,
      // Same lazy-load pattern as Blocked above — see inboxController.js's 'Groups'
      // onlyCategory fast path and the WhatsApp Groups plan's Phase G6.
      Groups: false,
    },
    searchResults: null,
    isSearching: false,
    loading: false,
    initialized: false,
    isLoadingMore: false,
    error: null,
  },
  reducers: {
    // Live "moved OUT of a cached Lifecycle stage" patch — see contact_lifecycle_updated's
    // handler in socketHandler.js. Pure local removal (no network call needed, we already
    // know which id to drop), applied to ANY cached stage's bucket, not just the one
    // currently on screen — keeps every visited-and-cached stage accurate in the background.
    // No-ops if that stageId was never cached (nothing to patch). The mirror-image "moved IN"
    // case needs a real row's worth of data this event doesn't carry, so that side either
    // re-fetches (if it's the currently-viewed stage) or invalidates the cache entry (if it's
    // a cached-but-not-viewed stage) — see socketHandler.js and invalidateLifecycleStageCache.
    removeContactFromLifecycleList(state, action) {
      const { contactId, stageId } = action.payload || {};
      if (!contactId || !stageId) return;
      const bucket = state.lifecycleByStage[stageId];
      if (!bucket) return;
      const before = bucket.data.length;
      bucket.data = bucket.data.filter(
        (c) => String(c.contactId || c.contactID) !== String(contactId)
      );
      const removed = before - bucket.data.length;
      if (removed > 0) {
        bucket.count = Math.max(0, (bucket.count || 0) - removed);
      }
    },
    // Removes a contact from every inbox list — used when a reassignment leaves this user
    // without permission to view it (see debouncedAddMessageCard in socketInboxUpdater.js).
    // Top-level counts are recomputed by the caller (calculateAndDispatchInboxCounts).
    removeContactFromInbox(state, action) {
      const { contactId } = action.payload || {};
      if (!contactId) return;
      const notTarget = (c) => String(c.contactId || c.contactID) !== String(contactId);
      const removeFrom = (list) => Array.isArray(list) ? list.filter(notTarget) : list;
      const removeFromCachedBucket = (bucket) => {
        if (!bucket || !Array.isArray(bucket.data)) return;
        const before = bucket.data.length;
        bucket.data = bucket.data.filter(notTarget);
        bucket.count = Math.max(0, (bucket.count || 0) - (before - bucket.data.length));
      };

      for (const category of Object.keys(state.data)) {
        if (category === "TeamMembers" || category === "Teams") {
          if (Array.isArray(state.data[category])) {
            for (const group of state.data[category]) group.data = removeFrom(group.data);
          }
        } else {
          state.data[category] = removeFrom(state.data[category]);
        }
      }
      for (const bucket of Object.values(state.lifecycleByStage)) removeFromCachedBucket(bucket);
      for (const bucket of Object.values(state.agentByAgentId)) removeFromCachedBucket(bucket);
    },
    // Drops a cached-but-not-currently-viewed stage's bucket entirely, so the next time the
    // agent opens it, one fresh fetch happens (same cost as a first-ever visit) instead of
    // showing data we know is now stale, or spending a background fetch keeping an invisible
    // view live. See socketHandler.js's "moved IN to a different, cached stage" branch.
    invalidateLifecycleStageCache(state, action) {
      const { stageId } = action.payload || {};
      if (!stageId) return;
      delete state.lifecycleByStage[stageId];
    },
    updateContactNameInInbox(state, action) {
      const { contactId, name } = action.payload || {};
      if (!contactId || !name) return;
      const patchList = (arr) => {
        if (!Array.isArray(arr)) return;
        for (const item of arr) {
          if (item.contactId === contactId) { item.name = name; }
        }
      };
      patchList(state.data.All);
      patchList(state.data.You);
      patchList(state.data.Active);
      patchList(state.data.Unassigned);
      patchList(state.data.Assigned);
      patchList(state.data.Closed);
      patchList(state.data.Blocked);
      if (Array.isArray(state.data.TeamMembers)) {
        for (const tm of state.data.TeamMembers) patchList(tm.data);
      }
      if (Array.isArray(state.data.Teams)) {
        for (const t of state.data.Teams) patchList(t.data);
      }

      // Same Lifecycle/Agent rail session-cache gap as updateAssignmentInInbox
      // below — a rename never moves a contact between stage/agent buckets,
      // so this is a pure in-place field patch, same as the flat categories above.
      for (const bucket of Object.values(state.lifecycleByStage)) {
        patchList(bucket?.data);
      }
      for (const bucket of Object.values(state.agentByAgentId)) {
        patchList(bucket?.data);
      }
    },
    // Patches a contact's Lifecycle stage in place across every bucket — same shape as
    // updateContactNameInInbox above (Lifecycle stage never moves a contact between
    // buckets the way conversation status does, so no bucket-membership logic needed,
    // unlike updateAssignmentInInbox below). Two callers: the Inbox list's own
    // LifecycleStageChip (immediately after a successful move) and socketHandler.js's
    // contact_lifecycle_updated handler (keeps the chip live when the stage changes from
    // elsewhere — AI, or a future chat-header dropdown — while this list is open).
    updateLifecycleStageInInbox(state, action) {
      const { contactId, lifecycleStageId } = action.payload || {};
      if (!contactId) return;
      const patchList = (arr) => {
        if (!Array.isArray(arr)) return;
        for (const item of arr) {
          if (item.contactId === contactId) { item.lifecycleStageId = lifecycleStageId ?? null; }
        }
      };
      patchList(state.data.All);
      patchList(state.data.You);
      patchList(state.data.Active);
      patchList(state.data.Unassigned);
      patchList(state.data.Assigned);
      patchList(state.data.Closed);
      patchList(state.data.Blocked);
      if (Array.isArray(state.data.TeamMembers)) {
        for (const tm of state.data.TeamMembers) patchList(tm.data);
      }
      if (Array.isArray(state.data.Teams)) {
        for (const t of state.data.Teams) patchList(t.data);
      }
    },
    updateAssignmentInInbox(state, action) {
      const { contactId, assignedTo, assignedToName, assignedToId, assignedToIsAi, status } = action.payload || {};
      if (!contactId) return;
      
      // Find the contact in All array to get full contact data
      let contactData = null;
      if (state.data.All && Array.isArray(state.data.All)) {
        contactData = state.data.All.find(item => item.contactId === contactId);
      }
      
      // If not found in All, try to find it in any category
      if (!contactData && state.data && typeof state.data === 'object') {
        for (const category in state.data) {
          const categoryData = state.data[category];
          if (categoryData && Array.isArray(categoryData)) {
            const found = categoryData.find(item => item.contactId === contactId);
            if (found) {
              contactData = found;
              break;
            }
          }
        }
      }
      
      const patchItem = (item) => {
        if (assignedTo !== undefined) item.assignedTo = assignedTo;
        if (assignedToName !== undefined) item.assignedToName = assignedToName;
        if (assignedToId !== undefined) item.assignedToId = assignedToId;
        // Lets the card show "(AI)" instead of reading like a same-named human/team
        // assignee (see MessageCard.jsx's assigneeIsAi) — defaults to false whenever
        // a real assignedTo lands without it, so a stale true from a previous AI
        // assignment can't linger through a later human reassignment.
        if (assignedTo !== undefined) item.assignedToIsAi = assignedToIsAi === true;
        if (status !== undefined) item.status = status;
        if (item.lastMessage) {
          if (assignedTo !== undefined) item.lastMessage.assignedTo = assignedTo;
          if (assignedToName !== undefined) item.lastMessage.assignedToName = assignedToName;
        }
      };
      const patchList = (arr) => {
        if (!Array.isArray(arr)) return;
        const idx = arr.findIndex(item => item.contactId === contactId);
        if (idx !== -1) patchItem(arr[idx]);
      };

      // Update flat categories
      patchList(state.data.All);
      patchList(state.data.You);
      patchList(state.data.Active);
      patchList(state.data.Unassigned);
      patchList(state.data.Assigned);
      patchList(state.data.Closed);
      
      // Get the updated contact from All array to check its final status
      const updatedContactInAll = state.data.All.find(item => item.contactId === contactId);
      
      // Determine the final status and assignedTo values
      const finalStatus = status !== undefined ? status : (updatedContactInAll?.status || contactData?.status || "Unassigned");
      const finalAssignedTo = assignedTo !== undefined ? assignedTo : (updatedContactInAll?.assignedTo || contactData?.assignedTo || null);
      const isUnassigned = finalStatus === "Unassigned" || !finalAssignedTo || finalAssignedTo === "Unassigned";
      
      // If status is Unassigned, ensure contact is in both Unassigned and All arrays
      if (isUnassigned && updatedContactInAll) {
        // Ensure in All array (should already be there, but double-check)
        if (!state.data.All) state.data.All = [];
        const allIndex = state.data.All.findIndex(c => c.contactId === contactId);
        if (allIndex === -1) {
          state.data.All.unshift(updatedContactInAll);
        }
        
        // Ensure in Unassigned array
        if (!state.data.Unassigned) state.data.Unassigned = [];
        const unassignedIndex = state.data.Unassigned.findIndex(c => c.contactId === contactId);
        if (unassignedIndex !== -1) {
          state.data.Unassigned[unassignedIndex] = updatedContactInAll;
        } else {
          state.data.Unassigned.unshift(updatedContactInAll);
        }
        
        // Remove from Closed array (unassigned contacts should not be in closed categories)
        if (state.data.Closed) {
          state.data.Closed = state.data.Closed.filter(c => c.contactId !== contactId);
        }
      }
      
      // Update nested TeamMembers and Teams if present
      if (Array.isArray(state.data.TeamMembers)) {
        for (const tm of state.data.TeamMembers) patchList(tm.data);
      }
      if (Array.isArray(state.data.Teams)) {
        for (const t of state.data.Teams) patchList(t.data);
      }

      // Also patch the Lifecycle rail's own per-stage session cache
      // (state.lifecycleByStage — see its comment in initialState) and the
      // equivalent per-agent cache. Assigning/closing a contact while its
      // Lifecycle-stage tab is cached (visited earlier this session, not
      // necessarily the one on screen right now) never touched these buckets
      // before, so MessageCard's assignee/status there stayed stale until a
      // full refetch — the flat categories above got the live patch, this
      // one didn't (contact stays in the same stage bucket either way, so a
      // plain in-place patch is all that's needed, same as the name patch).
      for (const bucket of Object.values(state.lifecycleByStage)) {
        patchList(bucket?.data);
      }
      for (const bucket of Object.values(state.agentByAgentId)) {
        patchList(bucket?.data);
      }
    },
    // We left / were removed from a WhatsApp group (server: `group_left` socket event): drop its card
    // from every category array (All, Groups, ...). The server already hides it from list fetches
    // via GroupModel.leftAt; this is the live counterpart so an open inbox doesn't keep showing it.
    removeGroupFromInbox(state, action) {
      const idStr = String(action.payload || "");
      if (!idStr || !state.data || typeof state.data !== "object") return;
      for (const category of Object.keys(state.data)) {
        const list = state.data[category];
        if (!Array.isArray(list)) continue;
        state.data[category] = list.filter((c) => {
          const cid = c?.contactId || c?.contactID;
          return !(cid && String(cid) === idStr);
        });
      }
    },
    addMessageCard: {
      reducer(state, action) {
      let contactData = action.payload;

      // Ensure state.data exists and is initialized
      if (!state.data) {
        state.data = {};
      }

      // Assignment update processing
      const contactId = contactData.contactId || contactData.contactID;
      if (!contactData || !contactId) {
        console.error("Invalid contact data received:", contactData);
        return;
      }

      // CRITICAL: Normalize contactId to string for consistent comparison throughout
      const contactIdStr = String(contactId);

      // Multi-number: with the Inbox filtered to ONE number, a live update is accepted only if it is provably for that number:
      //   - it carries a number and it is the selected one; or
      //   - it carries no number but that chat is ALREADY in the filtered list (so it is only an update to a card you see).
      // Anything else is dropped: another number's chat, and every chat that has no number at all (Meta, Instagram, Facebook,
      // website, Shopify) -- before this, an event without a number passed and could add or bump such a card. With no number
      // selected ("All numbers") nothing here applies.
      if (state.selectedConnectionId) {
        const eventNumber = contactData.connectionId;
        if (eventNumber) {
          if (String(eventNumber) !== String(state.selectedConnectionId)) return;
        } else if (!isContactInLoadedLists(state.data, contactIdStr)) {
          return;
        }
      }

      // A QR chat's card must say it is QR (number badge, no 24h clock) even when the event that creates or updates it is a
      // hand-built one without a platform (see withQrIdentity).
      contactData = withQrIdentity(state.data, contactIdStr, contactData);

      // Bug fix (2026-09-01, Rahim: "if last message was assigned to rahim what will
      // it show, nothing or automated?" — it showed nothing). A live socket update for
      // a log-only event (assignment/closechat/blockcontact/date — these never carry
      // real message content) was overwriting the Inbox list's preview text with blank,
      // discarding whatever the last REAL message actually said. The initial REST fetch
      // (server's inboxController.js) already filters these out and shows the last real
      // message instead ("Only actual sent/received messages qualify — never surface
      // log rows as the inbox preview") — socket updates need the same behavior.
      // Backfill just the content-describing fields from the contact's existing entry
      // BEFORE any branch below reads/spreads contactData.lastMessage, so every branch
      // naturally picks up the real content. Deliberately NOT touching `.type` (still
      // "assignment"/"closechat"/etc.) — that field drives real routing logic further
      // down (closed-tab placement, assignment badges) and must stay accurate; only
      // MessageCard.jsx's preview-type lookup needs `messageType`, which we do backfill.
      const LOG_ONLY_TYPES_FOR_PREVIEW = new Set(["assignment", "date", "closechat", "blockcontact"]);
      if (
        contactData.lastMessage &&
        LOG_ONLY_TYPES_FOR_PREVIEW.has(contactData.lastMessage.type) &&
        !contactData.lastMessage.content
      ) {
        let existingEntryForPreview = null;
        if (state.data && typeof state.data === "object") {
          for (const category in state.data) {
            const categoryData = state.data[category];
            if (categoryData && Array.isArray(categoryData)) {
              const found = categoryData.find((c) => {
                const cId = c?.contactId || c?.contactID;
                return cId && String(cId) === contactIdStr;
              });
              if (found) {
                existingEntryForPreview = found;
                break;
              }
            }
          }
        }
        const prevLastMessage = existingEntryForPreview?.lastMessage;
        const prevContent = prevLastMessage?.content || prevLastMessage?.body || prevLastMessage?.message;
        if (prevContent) {
          contactData.lastMessage.content = prevContent;
          if (prevLastMessage.messageType) contactData.lastMessage.messageType = prevLastMessage.messageType;
          if (prevLastMessage.sendername) contactData.lastMessage.sendername = prevLastMessage.sendername;
          if (prevLastMessage.performedBy !== undefined) contactData.lastMessage.performedBy = prevLastMessage.performedBy;
          if (prevLastMessage.performedByName) contactData.lastMessage.performedByName = prevLastMessage.performedByName;
          if (prevLastMessage.isWorkflowAutomated !== undefined) contactData.lastMessage.isWorkflowAutomated = prevLastMessage.isWorkflowAutomated;
          if (prevLastMessage.isBulkMessage !== undefined) contactData.lastMessage.isBulkMessage = prevLastMessage.isBulkMessage;
        }
      }

      // Block/unblock: handled as its own relocation, independent of the closeStatus/
      // assignment logic below (blockContact.service.js/unblockContact.service.js set
      // `isBlocked` explicitly on the socket payload — see socketMessageDispatcher.js).
      if (typeof contactData.isBlocked === 'boolean') {
        if (contactData.isBlocked) {
          let existingBlockContact = null;
          if (state.data && typeof state.data === 'object') {
            Object.keys(state.data).forEach(category => {
              const categoryData = state.data[category];
              if (categoryData && Array.isArray(categoryData)) {
                const found = categoryData.find(c => {
                  if (!c) return false;
                  const cId = c.contactId || c.contactID;
                  return cId && String(cId) === contactIdStr;
                });
                if (found) existingBlockContact = found;
              }
            });
          }

          const blockedContactData = {
            ...(existingBlockContact || {}),
            ...contactData,
            contactId: contactIdStr,
          };

          removeContactFromAllArrays(state, contactIdStr, 0);

          if (!state.data.Blocked) state.data.Blocked = [];
          const existingInBlocked = state.data.Blocked.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (existingInBlocked !== -1) {
            state.data.Blocked[existingInBlocked] = blockedContactData;
          } else {
            state.data.Blocked.unshift(blockedContactData);
          }
        } else {
          // Unblocked: remove from Blocked immediately so the tab/badge update in real
          // time. The acting user's own unblockContact thunk already dispatches
          // fetchInbox() to re-place the contact into its correct category; other
          // connected tabs pick that up on their next refresh.
          if (state.data.Blocked && Array.isArray(state.data.Blocked)) {
            state.data.Blocked = state.data.Blocked.filter(c => {
              const cId = c?.contactId || c?.contactID;
              return !cId || String(cId) !== contactIdStr;
            });
          }
        }

        // Force Blocked's pagination/count to match the array we just mutated —
        // selectInboxRecentCounts falls back to pagination.Blocked.totalCount whenever
        // hasMore is stale-true (e.g. left over from an earlier "N blocked, not yet
        // loaded" placeholder response), which otherwise kept showing the old count
        // after an unblock even though state.data.Blocked was already correct.
        if (!state.pagination) state.pagination = {};
        if (!state.pagination.Blocked) state.pagination.Blocked = { page: 1, limit: 50, hasMore: false, totalCount: 0 };
        state.pagination.Blocked.hasMore = false;
        state.pagination.Blocked.totalCount = state.data.Blocked.length;
        if (!state.counts) state.counts = {};
        state.counts.Blocked = state.data.Blocked.length;

        updateAllCounts(state);
        if (typeof window !== 'undefined' && window.dispatchRedux) {
          setTimeout(() => {
            calculateAndDispatchInboxCounts(window.dispatchRedux, state.data);
          }, 50);
        }
        return;
      }

      // Current user ID is resolved in prepare() below, not here — this reducer body
      // runs inside store.dispatch(), and Redux throws "You may not call
      // store.getState() while the reducer is executing" if getState() is called from
      // inside a reducer. That was happening here (silently swallowed by a try/catch),
      // so currentUserId was always null and the "You" tab never matched in real time.
      const currentUserId = action.meta?.currentUserId ?? null;
      // Get active member IDs from global (set this in your app root or via Redux selector)
      let activeMemberIds = [];
      if (window.activeMemberIds && Array.isArray(window.activeMemberIds)) {
        activeMemberIds = window.activeMemberIds;
      }

      // Robustly determine close status - check multiple sources for reliability
      let closeStatus = null;
      
      // Priority 1: Check closeStatus field (most explicit)
      if (contactData.closeStatus) {
        const cs = String(contactData.closeStatus).toLowerCase();
        if (cs === "closed") {
          closeStatus = cs;
        }
      }

      // Priority 2: Check status field if it's a closed status
      if (!closeStatus && contactData.status) {
        const s = String(contactData.status).toLowerCase();
        if (s === "closed") {
          closeStatus = s;
        }
      }

      // Priority 3: Check lastMessage for closechat type
      if (!closeStatus && contactData.lastMessage?.type === "closechat") {
        if (contactData.lastMessage.body) {
          const body = String(contactData.lastMessage.body).toLowerCase();
          if (body === "closed") {
            closeStatus = body;
          }
        }
        // If body doesn't have status, check status field again
        if (!closeStatus && contactData.status) {
          const s = String(contactData.status).toLowerCase();
          if (s === "closed") {
            closeStatus = s;
          }
        }
      }

      // Priority 4: Check chat type for closechat
      if (!closeStatus && contactData.chat?.type === "closechat") {
        if (contactData.chat.body) {
          const body = String(contactData.chat.body).toLowerCase();
          if (body === "closed") {
            closeStatus = body;
          }
        }
      }

      // CRITICAL: If contact sends a message and status is Unassigned, move from Closed to Unassigned
      // This handles the case where a closed contact sends a message and backend moves them to Unassigned
      // BUT: For internal mentions, check existing contact assignment, not incoming data (which excludes assignment)
      const isInternalMentionCheck = contactData.lastMessage?.sendername && 
                                    contactData.lastMessage?.sendername !== 'Automated' &&
                                    !contactData.lastMessage?.isWorkflowAutomated &&
                                    (contactData.lastMessage?.status === 'internal' || 
                                     contactData.chat?.status === 'internal' ||
                                     contactData.status === 'internal');
      
      if (!closeStatus) {
        // For internal mentions, check existing contact assignment instead of incoming data
        let existingContactForCheck = null;
        if (isInternalMentionCheck) {
          // Check All array first
          if (state.data.All) {
            existingContactForCheck = state.data.All.find(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
          }
          // If not in All, check Assigned array
          if (!existingContactForCheck && state.data.Assigned) {
            existingContactForCheck = state.data.Assigned.find(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
          }
          // Also check contactInfo from Redux state
          if (!existingContactForCheck && typeof window !== 'undefined' && window.store && typeof window.store.getState === 'function') {
            try {
              const globalState = window.store.getState();
              const contactInfo = globalState?.inboxInfo?.contactInfo;
              if (contactInfo && (String(contactInfo._id) === contactIdStr || String(contactInfo.contactId) === contactIdStr || String(contactInfo.contactID) === contactIdStr)) {
                existingContactForCheck = contactInfo;
              }
            } catch (e) {
              // Silently fail if store is not available
            }
          }
        }
        
        const contactStatus = contactData.status ? String(contactData.status) : null;
        // For internal mentions, use existing contact's assignment status, not incoming data
        const assignedToForUnassignedCheck = isInternalMentionCheck && existingContactForCheck
          ? (existingContactForCheck.assignedTo || existingContactForCheck.lastMessage?.assignedTo)
          : contactData.assignedTo;
        const statusForUnassignedCheck = isInternalMentionCheck && existingContactForCheck
          ? (existingContactForCheck.status || "Assigned")
          : contactStatus;
        
        const isUnassignedStatus = statusForUnassignedCheck === "Unassigned" || 
                                   !assignedToForUnassignedCheck || 
                                   assignedToForUnassignedCheck === "Unassigned" ||
                                   assignedToForUnassignedCheck === null;
        
        // Check if contact is currently in the Closed array
        let existingClosedContact = null;
        ["Closed"].forEach(closedCat => {
          if (state.data[closedCat] && Array.isArray(state.data[closedCat])) {
            const idx = state.data[closedCat].findIndex(contact => {
              const cId = contact?.contactId || contact?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (idx !== -1) {
              existingClosedContact = state.data[closedCat][idx];
              state.data[closedCat].splice(idx, 1);
            }
          }
        });

        // If contact was in closed array and now has Unassigned status, move to Unassigned
        // CRITICAL: For internal mentions, skip this logic - internal mentions should preserve assignment
        if (existingClosedContact && isUnassignedStatus && !isInternalMentionCheck) {
          // Merge existing data with new message data
          const safeContactData = {
            ...existingClosedContact,
            ...contactData,
            status: "Unassigned",
            assignedTo: null,
            assignedToName: null,
            lastMessage: {
              ...(existingClosedContact.lastMessage || {}),
              ...(contactData.lastMessage || {}),
              timestamp: ensureSerializableTimestamp(contactData.lastMessage?.timestamp || contactData.chat?.timestamp),
            },
            name: contactData.name || existingClosedContact.name || "Unknown",
            activeStatus: contactData.activeStatus !== undefined
              ? contactData.activeStatus
              : existingClosedContact.activeStatus
          };

          // Add to All array
          if (!state.data.All) state.data.All = [];
          const allIdx = state.data.All.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (allIdx !== -1) {
            state.data.All[allIdx] = {
              ...state.data.All[allIdx],
              ...safeContactData
            };
          } else {
            state.data.All.unshift(safeContactData);
          }

          // Add to Unassigned array
          if (!state.data.Unassigned) state.data.Unassigned = [];
          const unassignedIdx = state.data.Unassigned.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (unassignedIdx !== -1) {
            state.data.Unassigned[unassignedIdx] = {
              ...state.data.Unassigned[unassignedIdx],
              ...safeContactData
            };
          } else {
            state.data.Unassigned.unshift(safeContactData);
          }

          // Remove from Assigned if present
          if (state.data.Assigned) {
            state.data.Assigned = state.data.Assigned.filter(c => {
              const cId = c?.contactId || c?.contactID;
              return !cId || String(cId) !== contactIdStr;
            });
          }

          // Remove from You if present (since it's unassigned)
          if (state.data.You) {
            state.data.You = state.data.You.filter(c => {
              const cId = c?.contactId || c?.contactID;
              return !cId || String(cId) !== contactIdStr;
            });
          }

          // Update Active array if needed
          if (safeContactData.activeStatus > 0) {
            if (!state.data.Active) state.data.Active = [];
            const activeIdx = state.data.Active.findIndex(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (activeIdx !== -1) {
              state.data.Active[activeIdx] = safeContactData;
            } else {
              state.data.Active.unshift(safeContactData);
            }
          }

          // Update team assignments
          const oldAssignedTo = existingClosedContact.assignedTo || existingClosedContact.lastMessage?.assignedTo;
          updateTeamAssignment(state, contactId, null, oldAssignedTo, safeContactData);

          // Recalculate counts
          updateAllCounts(state);
          
          // Trigger count update dispatch
          if (typeof window !== 'undefined' && window.dispatchRedux) {
            setTimeout(() => {
              calculateAndDispatchInboxCounts(window.dispatchRedux, state.data);
            }, 50);
          }
          
          return; // Exit early since we've handled the transition
        }
      }

      // --- NEW LOGIC: Assignment for closed chats ---
      if (contactData.lastMessage?.type === "assignment" || contactData.chat?.type === "assignment") {
        // Remove from Closed
        let existingClosedContact = null;
        ["Closed"].forEach(closedCat => {
          if (state.data[closedCat] && Array.isArray(state.data[closedCat])) {
            const idx = state.data[closedCat].findIndex(contact => {
              const cId = contact?.contactId || contact?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (idx !== -1) {
              existingClosedContact = state.data[closedCat][idx];
              state.data[closedCat].splice(idx, 1);
            }
          }
        });

        if (existingClosedContact) {
          // CRITICAL: Check if this is an internal mention message
          // Internal mentions should preserve existing assignment status, not overwrite it
          const isInternalMentionClosed = contactData.lastMessage?.sendername && 
                                         contactData.lastMessage?.sendername !== 'Automated' &&
                                         !contactData.lastMessage?.isWorkflowAutomated &&
                                         (contactData.lastMessage?.status === 'internal' || 
                                          contactData.chat?.status === 'internal' ||
                                          contactData.status === 'internal');
          
          // CRITICAL: For internal mentions, also check contactInfo from Redux state for current assignment
          // This ensures we get the most up-to-date assignment even if arrays are stale
          let existingContactInfoClosed = null;
          if (isInternalMentionClosed && typeof window !== 'undefined' && window.store && typeof window.store.getState === 'function') {
            try {
              const globalState = window.store.getState();
              const contactInfo = globalState?.inboxInfo?.contactInfo;
              if (contactInfo && (String(contactInfo._id) === contactIdStr || String(contactInfo.contactId) === contactIdStr || String(contactInfo.contactID) === contactIdStr)) {
                existingContactInfoClosed = contactInfo;
              }
            } catch (e) {
              // Silently fail if store is not available
            }
          }
          
          // CRITICAL: For internal mentions, exclude status and assignedTo from contactData spread
          // to prevent undefined values from overwriting existing assignment
          const contactDataToSpreadClosed = isInternalMentionClosed
            ? Object.fromEntries(
                Object.entries(contactData).filter(([key]) => 
                  !['status', 'assignedTo', 'assignedToName', 'assignedToId'].includes(key)
                )
              )
            : contactData;
          
          // Merge all previous values with new assignment data
          const safeContactData = {
            ...existingClosedContact,
            ...contactDataToSpreadClosed, // overwrite with new incoming data (excluding assignment fields for internal mentions)
            // CRITICAL: For internal mentions, ALWAYS preserve existing status and assignment fields
            // For regular messages, use provided values or fallback to existing or defaults
            // For internal mentions, prioritize contactInfo (most current), then existingClosedContact
            status: isInternalMentionClosed
              ? (existingContactInfoClosed?.status || existingClosedContact.status || "Assigned") // Always use existing status for internal mentions
              : (contactData.status || existingClosedContact.status || "Assigned"), // Use actual status from socket data
            assignedTo: isInternalMentionClosed
              ? (existingContactInfoClosed?.assignedTo || existingClosedContact.assignedTo || null) // Always use existing assignedTo for internal mentions
              : (contactData.assignedTo || existingClosedContact.assignedTo || null), // ID
            assignedToName: isInternalMentionClosed
              ? (existingContactInfoClosed?.assignedToName || existingClosedContact.assignedToName || null) // Always use existing assignedToName for internal mentions
              : (contactData.assignedToName || existingClosedContact.assignedToName || null), // Display name
            lastMessage: {
              ...(contactData.lastMessage || {}),
              timestamp: ensureSerializableTimestamp(contactData.lastMessage?.timestamp),
              // CRITICAL: For internal mentions, ALWAYS preserve existing assignment in lastMessage
              // For regular messages, use provided values or fallback to existing
              // For internal mentions, prioritize contactInfo (most current), then existingClosedContact
              assignedTo: isInternalMentionClosed
                ? (existingContactInfoClosed?.lastMessage?.assignedTo || existingContactInfoClosed?.assignedTo || existingClosedContact.lastMessage?.assignedTo || existingClosedContact.assignedTo || null) // Always use existing assignedTo for internal mentions
                : (contactData.assignedTo || existingClosedContact.assignedTo || null), // ID
              assignedToName: isInternalMentionClosed
                ? (existingContactInfoClosed?.lastMessage?.assignedToName || existingContactInfoClosed?.assignedToName || existingClosedContact.lastMessage?.assignedToName || existingClosedContact.assignedToName || null) // Always use existing assignedToName for internal mentions
                : (contactData.assignedToName || existingClosedContact.assignedToName || null), // Display name
              name: contactData.name || existingClosedContact.name || "Unknown"
            },
            name: contactData.name || existingClosedContact.name || "Unknown",
            activeStatus: contactData.activeStatus !== undefined
              ? contactData.activeStatus
              : existingClosedContact.activeStatus
          };
          // Add or update in All (assignment reopens closed contact, so it's OK to add to All)
          // But ensure we use string comparison for reliable matching
          if (!state.data.All) state.data.All = [];
          const allIdx = state.data.All.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (allIdx !== -1) {
            state.data.All[allIdx] = {
              ...state.data.All[allIdx],
              ...safeContactData
            };
          } else {
            state.data.All.unshift(safeContactData);
          }
          // --- Update 'You' category ---
          const assignedTo = safeContactData.assignedTo || safeContactData.lastMessage?.assignedTo;
          const isYou = (currentUserId && assignedTo === currentUserId) || (activeMemberIds.length > 0 && activeMemberIds.includes(assignedTo));
          if (isYou) {
            if (!state.data.You) state.data.You = [];
            const youIdx = state.data.You.findIndex(c => c.contactId === contactId);
            if (youIdx === -1) {
              state.data.You.unshift(safeContactData);
            } else {
              state.data.You[youIdx] = {
                ...state.data.You[youIdx],
                ...safeContactData
              };
            }
          } else {
            if (state.data.You) {
              state.data.You = state.data.You.filter(c => c.contactId !== contactId);
            }
          }
          
          // Update team assignment
          const oldAssignedTo = existingClosedContact.assignedTo || existingClosedContact.lastMessage?.assignedTo;
          const newAssignedTo = safeContactData.assignedTo || safeContactData.lastMessage?.assignedTo;
          updateTeamAssignment(state, contactId, newAssignedTo, oldAssignedTo, safeContactData);
          
        // Recalculate counts including team counts
        updateAllCounts(state);
        
        // Trigger count update dispatch after state is updated
        if (typeof window !== 'undefined' && window.dispatchRedux) {
          setTimeout(() => {
            calculateAndDispatchInboxCounts(window.dispatchRedux, state.data);
          }, 50);
        }
        return;
        }

        // ELSE: If not in closed, update/add in All with assignment data
        // CRITICAL: Check if contact is closed before adding to All
        const isCurrentlyClosed = state.data.Closed?.some(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });

        // Get existing contact from All array first (needed for safeContactData)
        if (!state.data.All) state.data.All = [];
        const allIdx = state.data.All.findIndex(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });
        const existingAllContact = state.data.All[allIdx] || {};
        
        // CRITICAL: For internal mentions, check ALL arrays to get existing assignment status
        // This ensures we preserve assignment even if contact is not in All array yet
        let existingAssignedContact = null;
        if (state.data.Assigned) {
          existingAssignedContact = state.data.Assigned.find(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
        }
        
        // CRITICAL: Also check Unassigned array to preserve unassigned status for internal mentions
        let existingUnassignedContact = null;
        if (state.data.Unassigned) {
          existingUnassignedContact = state.data.Unassigned.find(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
        }
        
        // CRITICAL: Also check contactInfo from Redux state for current assignment
        // This ensures we get the most up-to-date assignment even if arrays are stale
        let existingContactInfo = null;
        if (typeof window !== 'undefined' && window.store && typeof window.store.getState === 'function') {
          try {
            const globalState = window.store.getState();
            const contactInfo = globalState?.inboxInfo?.contactInfo;
            if (contactInfo && (String(contactInfo._id) === contactIdStr || String(contactInfo.contactId) === contactIdStr || String(contactInfo.contactID) === contactIdStr)) {
              existingContactInfo = contactInfo;
            }
          } catch (e) {
            // Silently fail if store is not available
          }
        }
        
        // CRITICAL: Check if this is an internal mention message
        // Internal mentions should preserve existing assignment status, not overwrite it
        const isInternalMention = contactData.lastMessage?.sendername && 
                                  contactData.lastMessage?.sendername !== 'Automated' &&
                                  !contactData.lastMessage?.isWorkflowAutomated &&
                                  (contactData.lastMessage?.status === 'internal' || 
                                   contactData.chat?.status === 'internal' ||
                                   contactData.status === 'internal');
        
        // CRITICAL: For internal mentions, exclude status and assignedTo from contactData spread
        // to prevent undefined values from overwriting existing assignment
        const contactDataToSpread = isInternalMention
          ? Object.fromEntries(
              Object.entries(contactData).filter(([key]) => 
                !['status', 'assignedTo', 'assignedToName', 'assignedToId'].includes(key)
              )
            )
          : contactData;
        
        // Create safeContactData (needed for all subsequent operations)
        const safeContactData = {
          ...existingAllContact, // fallback to existing data for missing fields
          ...contactDataToSpread, // overwrite with new incoming data (excluding assignment fields for internal mentions)
          // CRITICAL: For internal mentions, ALWAYS preserve existing status and assignment fields
          // For regular messages, use provided values or fallback to existing or defaults
          // For internal mentions, prioritize contactInfo (most current), then Assigned array, then All array
          // If contact is in Unassigned array and not found elsewhere, preserve "Unassigned" status
          // Otherwise, if found in Assigned or has assignment data, preserve "Assigned" status
          status: isInternalMention 
            ? (existingContactInfo?.status || existingAssignedContact?.status || existingAllContact.status || (existingUnassignedContact ? "Unassigned" : existingAllContact.status || "Assigned")) // Always use existing status for internal mentions
            : (contactData.status || existingAllContact.status || "Assigned"), // Use the actual status from socket data
          assignedTo: isInternalMention
            ? (existingContactInfo?.assignedTo || existingAssignedContact?.assignedTo || existingAllContact.assignedTo || (existingUnassignedContact ? null : existingAllContact.assignedTo || null)) // Always use existing assignedTo for internal mentions
            : (contactData.assignedTo || existingAllContact.assignedTo || null), // ID
          assignedToName: isInternalMention
            ? (existingContactInfo?.assignedToName || existingAssignedContact?.assignedToName || existingAllContact.assignedToName || null) // Always use existing assignedToName for internal mentions
            : (contactData.assignedToName || existingAllContact.assignedToName || null), // Display name
          lastMessage: {
            ...(existingAllContact.lastMessage || {}),
            ...(contactData.lastMessage || {}),
            timestamp: ensureSerializableTimestamp(contactData.lastMessage?.timestamp || contactData.chat?.timestamp),
            // CRITICAL: For internal mentions, ALWAYS preserve existing assignment in lastMessage
            // For regular messages, use provided values or fallback to existing
            // For internal mentions, prioritize contactInfo (most current), then Assigned array, then All array
            assignedTo: isInternalMention
              ? (existingContactInfo?.lastMessage?.assignedTo || existingContactInfo?.assignedTo || existingAssignedContact?.lastMessage?.assignedTo || existingAssignedContact?.assignedTo || existingAllContact.lastMessage?.assignedTo || existingAllContact.assignedTo || null) // Always use existing assignedTo for internal mentions
              : (contactData.assignedTo || existingAllContact.lastMessage?.assignedTo || null), // ID
            assignedToName: isInternalMention
              ? (existingContactInfo?.lastMessage?.assignedToName || existingContactInfo?.assignedToName || existingAssignedContact?.lastMessage?.assignedToName || existingAssignedContact?.assignedToName || existingAllContact.lastMessage?.assignedToName || existingAllContact.assignedToName || null) // Always use existing assignedToName for internal mentions
              : (contactData.assignedToName || existingAllContact.lastMessage?.assignedToName || null), // Display name
            name: contactData.name || existingAllContact.name || "Unknown"
          },
          name: contactData.name || existingAllContact.name || "Unknown",
          activeStatus: contactData.activeStatus !== undefined
            ? contactData.activeStatus
            : existingAllContact.activeStatus || 0
        };

        // Only add to All if NOT closed
        if (!isCurrentlyClosed) {
          if (allIdx !== -1) {
            state.data.All[allIdx] = {
              ...state.data.All[allIdx],
              ...safeContactData
            };
          } else {
            state.data.All.unshift(safeContactData);
          }
        } else {
          // If closed, ensure it's removed from All
          if (allIdx !== -1) {
            state.data.All.splice(allIdx, 1);
          }
        }
        
        // --- Update 'You' category ---
        const assignedTo = safeContactData.assignedTo || safeContactData.lastMessage?.assignedTo;
        const isYou = (currentUserId && assignedTo === currentUserId) || (activeMemberIds.length > 0 && activeMemberIds.includes(assignedTo));
        if (isYou) {
          if (!state.data.You) state.data.You = [];
          const youIdx = state.data.You.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (youIdx === -1) {
            state.data.You.unshift(safeContactData);
          } else {
            state.data.You[youIdx] = {
              ...state.data.You[youIdx],
              ...safeContactData
            };
          }
        } else {
          if (state.data.You) {
            state.data.You = state.data.You.filter(c => {
              const cId = c?.contactId || c?.contactID;
              return !cId || String(cId) !== contactIdStr;
            });
          }
        }
        
        // CRITICAL: Check if contact is currently in Closed BEFORE processing assignment
        // Closed contacts should NEVER be moved from closed arrays, even if workflow messages change status
        const isCurrentlyInClosed = state.data.Closed?.some(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });
        
        // Determine if contact is assigned (has assignedTo and it's not "Unassigned")
        // CRITICAL: For internal mentions, use safeContactData values directly to ensure we use preserved assignment
        const assignedToForCheck = isInternalMention ? safeContactData.assignedTo : assignedTo;
        const statusForCheck = isInternalMention ? safeContactData.status : safeContactData.status;
        
        // CRITICAL: For internal mentions, if assignment data is missing, check if contact is currently in Assigned array
        // This prevents internal mentions from incorrectly moving contacts to Unassigned when assignment data is missing
        let finalAssignedToForCheck = assignedToForCheck;
        let finalStatusForCheck = statusForCheck;
        if (isInternalMention && !assignedToForCheck && statusForCheck !== "Unassigned") {
          // Check if contact is currently in Assigned array - if so, preserve "Assigned" status
          if (state.data.Assigned) {
            const currentlyAssigned = state.data.Assigned.find(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (currentlyAssigned && currentlyAssigned.assignedTo) {
              finalAssignedToForCheck = currentlyAssigned.assignedTo;
              finalStatusForCheck = currentlyAssigned.status || "Assigned";
            }
          }
        }
        
        const isAssigned = finalAssignedToForCheck && finalAssignedToForCheck !== "Unassigned" && finalStatusForCheck !== "Unassigned";
        // Determine if contact is unassigned (no assignedTo or assignedTo is "Unassigned" or status is "Unassigned")
        // CRITICAL: For internal mentions, only mark as unassigned if status is explicitly "Unassigned" or contact is in Unassigned array
        const isUnassigned = isInternalMention 
          ? (finalStatusForCheck === "Unassigned" || (state.data.Unassigned && state.data.Unassigned.some(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            })))
          : (!finalAssignedToForCheck || finalAssignedToForCheck === "Unassigned" || finalStatusForCheck === "Unassigned");
        
        // --- Update 'Assigned' category ---
        // Only add to Assigned if NOT closed and IS assigned
        if (!isCurrentlyInClosed && isAssigned) {
          if (!state.data.Assigned) state.data.Assigned = [];
          const assignedIdx = state.data.Assigned.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (assignedIdx === -1) {
            state.data.Assigned.unshift(safeContactData);
          } else {
            state.data.Assigned[assignedIdx] = {
              ...state.data.Assigned[assignedIdx],
              ...safeContactData
            };
          }
        } else {
          // Remove from Assigned category if not assigned OR if closed
          if (state.data.Assigned) {
            state.data.Assigned = state.data.Assigned.filter(c => {
              const cId = c?.contactId || c?.contactID;
              return !cId || String(cId) !== contactIdStr;
            });
          }
        }
        
        // --- Update 'Unassigned' category ---
        // Only add to Unassigned if NOT closed and IS unassigned
        if (!isCurrentlyInClosed && isUnassigned) {
          if (!state.data.Unassigned) state.data.Unassigned = [];
          const unassignedIdx = state.data.Unassigned.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (unassignedIdx === -1) {
            state.data.Unassigned.unshift(safeContactData);
          } else {
            state.data.Unassigned[unassignedIdx] = {
              ...state.data.Unassigned[unassignedIdx],
              ...safeContactData
            };
          }
        } else {
          // Remove from Unassigned category if assigned OR if closed
          if (state.data.Unassigned) {
            state.data.Unassigned = state.data.Unassigned.filter(c => {
              const cId = c?.contactId || c?.contactID;
              return !cId || String(cId) !== contactIdStr;
            });
          }
        }
        
        // --- Update 'Active' category ---
        // Active: contacts where conversation has started and less than 24 hours have passed (activeStatus > 0)
        // This applies regardless of assignment status (assigned/unassigned/closed)
        if (!state.data.Active) state.data.Active = [];
        const activeIdx = state.data.Active.findIndex(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });
        if (safeContactData.activeStatus > 0) {
          // Contact is active (conversation started and < 24 hours)
          if (activeIdx !== -1) {
            state.data.Active[activeIdx] = safeContactData;
          } else {
            state.data.Active.unshift(safeContactData);
          }
        } else {
          // Contact is not active (no conversation or > 24 hours), remove from Active
          if (activeIdx !== -1) {
            state.data.Active.splice(activeIdx, 1);
          }
        }
        
        // Update team assignment (only if NOT closed)
        // Closed contacts should not be in team assignment arrays
        if (!isCurrentlyInClosed) {
          const oldAssignedTo = existingAllContact.assignedTo || existingAllContact.lastMessage?.assignedTo;
          const newAssignedTo = safeContactData.assignedTo || safeContactData.lastMessage?.assignedTo;
          updateTeamAssignment(state, contactId, newAssignedTo, oldAssignedTo, safeContactData);
        }
        
        // Recalculate counts including team counts
        updateAllCounts(state);
        
        // Trigger count update dispatch after state is updated
        if (typeof window !== 'undefined' && window.dispatchRedux) {
          setTimeout(() => {
            calculateAndDispatchInboxCounts(window.dispatchRedux, state.data);
          }, 50);
        }
        return;
      }

      if (closeStatus === "closed") {
        // First find the existing contact to preserve its data
        // Use string comparison for reliable matching
        let existingContact = null;
        if (state.data && typeof state.data === 'object') {
          Object.keys(state.data).forEach(category => {
            const categoryData = state.data[category];
            if (categoryData && Array.isArray(categoryData)) {
              const found = categoryData.find(c => {
                if (!c) return false;
                const cId = c.contactId || c.contactID;
                return cId && String(cId) === contactIdStr;
              });
              if (found) {
                existingContact = found;
              }
            }
          });
        }

        const closeCategory = closeStatus.charAt(0).toUpperCase() + closeStatus.slice(1);
        
        // Merge existing data with new data
        const closedContactData = {
          ...(existingContact || {}), // Keep existing data
          ...contactData, // Overwrite with new data from payload
          status: closeCategory,
          closeStatus: closeCategory,
          // Ensure closed contacts do NOT carry assignment
          assignedTo: null,
          assignedToName: null,
          // Preserve activeStatus from existing contact (don't set to 0 at runtime)
          activeStatus: existingContact?.activeStatus ?? contactData.activeStatus ?? contactData.hoursRemaining ?? contactData.hoursLeft ?? 0,
          lastMessage: {
            ...((existingContact && existingContact.lastMessage) || {}),
            ...(contactData.lastMessage || {}),
            assignedTo: null,
            assignedToName: null,
          }
        };

        // SIMPLE: Remove from arrays (All, You, Unassigned, Assigned, TeamMembers, Teams, and other closed category)
        // Active is handled separately - only remove if activeStatus is 0
        removeContactFromAllArrays(state, contactIdStr, closedContactData.activeStatus);

        // Add ONLY to the appropriate closed category
        if (!state.data[closeCategory]) state.data[closeCategory] = [];
        const existingInClosed = state.data[closeCategory].findIndex(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });
        if (existingInClosed !== -1) {
          state.data[closeCategory][existingInClosed] = closedContactData;
        } else {
          state.data[closeCategory].unshift(closedContactData);
          bumpWithheldCategoryTotal(state, closeCategory, contactIdStr);
        }

        // Update Active array based on activeStatus
        if (!state.data.Active) state.data.Active = [];
        const activeIndex = state.data.Active.findIndex(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });
        if (closedContactData.activeStatus > 0) {
          // Keep in Active if activeStatus > 0
          if (activeIndex !== -1) {
            state.data.Active[activeIndex] = closedContactData;
          } else {
            state.data.Active.unshift(closedContactData);
          }
        } else {
          // Remove from Active if activeStatus is 0
          if (activeIndex !== -1) {
            state.data.Active.splice(activeIndex, 1);
          }
        }

        // CRITICAL: Multiple safety checks to ensure contact is NOT in All array
        // Check 1: Remove using string comparison
        if (state.data.All && Array.isArray(state.data.All)) {
          state.data.All = state.data.All.filter(c => {
            if (!c) return true;
            const cId = c.contactId || c.contactID;
            return !cId || String(cId) !== contactIdStr;
          });
        }
        // Check 2: Final verification - if still exists, force remove
        if (state.data.All && Array.isArray(state.data.All)) {
          const allIndex = state.data.All.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          if (allIndex !== -1) {
            state.data.All.splice(allIndex, 1);
          }
        }

        // Recalculate counts including team counts
        updateAllCounts(state);
        
        // Trigger count update dispatch after state is updated
        if (typeof window !== 'undefined' && window.dispatchRedux) {
          setTimeout(() => {
            calculateAndDispatchInboxCounts(window.dispatchRedux, state.data);
          }, 50);
        }
        return;
      }

      // Get the contact categories it belongs to

      // First, find and update the contact in its current location
      let existingContact = null;
      let existingContactIndex = -1;
      let sourceCategory = '';

      if (state.data && typeof state.data === 'object') {
        for (const category in state.data) {
          const categoryData = state.data[category];
          if (categoryData && Array.isArray(categoryData)) {
            const index = categoryData.findIndex(c => {
              if (!c) return false;
              const cId = c.contactId || c.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (index !== -1) {
              existingContact = categoryData[index];
              existingContactIndex = index;
              sourceCategory = category;
              break;
            }
          }
        }
      }

      // If contact is found, update it.
      if (existingContact) {
        const oldReceivedCount = existingContact.receivedCount || 0;
        // Only zero unreads when the payload clearly means “marked read” / thread closed.
        // Many socket cards use receivedCount: 0 for “no increment” (e.g. assignment) — those must not wipe badges.
        const lastMessageType = contactData.lastMessage?.type;
        const shouldClearUnread =
          contactData.markStatus === "read" ||
          lastMessageType === "read_update" ||
          lastMessageType === "closechat";
        const newReceivedCount = shouldClearUnread
          ? 0
          : contactData.receivedCount
            ? oldReceivedCount + contactData.receivedCount
            : oldReceivedCount;
        
        
        // Determine which lastMessage is more recent
        let latestLastMessage = existingContact.lastMessage;
        if (contactData.lastMessage?.timestamp && existingContact.lastMessage?.timestamp) {
          const newTimestamp = new Date(contactData.lastMessage.timestamp);
          const existingTimestamp = new Date(existingContact.lastMessage.timestamp);
          if (newTimestamp > existingTimestamp) {
            latestLastMessage = contactData.lastMessage;
          }
        } else if (contactData.lastMessage?.timestamp) {
          // If new message has timestamp but existing doesn't, use new
          latestLastMessage = contactData.lastMessage;
        } else if (contactData.lastMessage && !contactData.lastMessage.timestamp) {
          // If new message doesn't have timestamp, merge it but keep existing timestamp
          latestLastMessage = { ...contactData.lastMessage, timestamp: existingContact.lastMessage?.timestamp };
        } else if (contactData.lastMessage) {
          // CRITICAL: If new message has content but no timestamp, still update if it's more recent
          // This ensures internal mentions update the recent list even if timestamp comparison fails
          latestLastMessage = contactData.lastMessage;
        }
        
        // Calculate activeStatus from hoursRemaining or use provided activeStatus
        const calculatedActiveStatus = contactData.activeStatus !== undefined 
          ? contactData.activeStatus 
          : (contactData.hoursRemaining || contactData.hoursLeft || existingContact.activeStatus || existingContact.hoursRemaining || 0);
        
        const updatedContact = {
              ...existingContact,
          ...contactData,
          lastMessage: latestLastMessage,
          hoursRemaining: contactData.hoursRemaining || contactData.hoursLeft || existingContact.hoursRemaining,
          activeStatus: calculatedActiveStatus, // Ensure activeStatus is set
          receivedCount: newReceivedCount,
          // Preserve platform so Facebook/Instagram keep correct icon when socket payload omits it
          platform: contactData.platform ?? existingContact.platform ?? "whatsapp",
        };

        // If it's a close action, move it. Otherwise, just update in place.
        if (closeStatus) {
          const closeCategory = closeStatus.charAt(0).toUpperCase() + closeStatus.slice(1);
          updatedContact.status = closeCategory;
          updatedContact.closeStatus = closeCategory;
          // Clear any assignment when closed
          updatedContact.assignedTo = null;
          updatedContact.assignedToName = null;
          // Preserve activeStatus (don't set to 0 at runtime - let server handle it on refresh)
          // activeStatus is already set from calculatedActiveStatus above
          if (updatedContact.lastMessage) {
            updatedContact.lastMessage.assignedTo = null;
            updatedContact.lastMessage.assignedToName = null;
          }

          // SIMPLE: Remove from arrays (All, You, Unassigned, Assigned, TeamMembers, Teams, and other closed category)
          // Active is handled separately - only remove if activeStatus is 0
          removeContactFromAllArrays(state, contactIdStr, updatedContact.activeStatus);

          // Add ONLY to the appropriate closed category
          if (!state.data[closeCategory]) state.data[closeCategory] = [];
          const existingInClosed = state.data[closeCategory].findIndex(c => c && c.contactId === contactId);
          if (existingInClosed !== -1) {
            state.data[closeCategory][existingInClosed] = updatedContact;
          } else {
            state.data[closeCategory].unshift(updatedContact);
            bumpWithheldCategoryTotal(state, closeCategory, contactIdStr);
          }

          // Update Active array based on activeStatus
          if (!state.data.Active) state.data.Active = [];
          const activeIndex = state.data.Active.findIndex(c => c && c.contactId === contactId);
          if (updatedContact.activeStatus > 0) {
            // Keep in Active if activeStatus > 0
            if (activeIndex !== -1) {
              state.data.Active[activeIndex] = updatedContact;
            } else {
              state.data.Active.unshift(updatedContact);
            }
          } else {
            // Remove from Active if activeStatus is 0
            if (activeIndex !== -1) {
              state.data.Active.splice(activeIndex, 1);
            }
          }

          // CRITICAL: Double-check and ensure contact is NOT in All array (safety check)
          if (state.data.All && Array.isArray(state.data.All)) {
            const contactIdStr = String(contactId);
            const allIndex = state.data.All.findIndex(c => c && c.contactId && String(c.contactId) === contactIdStr);
            if (allIndex !== -1) {
              state.data.All.splice(allIndex, 1);
            }
          }

        } else {
          // It's a regular update, so just update it in its original category
          state.data[sourceCategory][existingContactIndex] = updatedContact;

          // CRITICAL: Check if contact is closed before adding/updating in All
          // This prevents closed contacts from being re-added to All by subsequent events
          const isCurrentlyClosed = state.data.Closed?.some(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });

          if (!isCurrentlyClosed) {
            // Only add/update in All if NOT closed
            if (!state.data.All) state.data.All = [];
            const allIndex = state.data.All.findIndex(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (allIndex !== -1) {
               state.data.All[allIndex] = updatedContact;
            } else {
              state.data.All.unshift(updatedContact);
            }
          } else {
            // If closed, ensure it's removed from All
            if (state.data.All && Array.isArray(state.data.All)) {
              const allIndex = state.data.All.findIndex(c => {
                const cId = c?.contactId || c?.contactID;
                return cId && String(cId) === contactIdStr;
              });
              if (allIndex !== -1) {
                state.data.All.splice(allIndex, 1);
              }
            }
          }

          // --- Sync into 'Groups' category (WhatsApp Groups plan) ---
          // A group card lives in both 'All' (synced above) and 'Groups' at once, but this
          // reducer only ever patched 'All' on a live socket push -- the Groups tab stayed
          // stale until a manual tab reselect/refresh re-fetched it from the DB (bug found
          // 2026-09-25 during live group testing). Purely additive: no effect on non-group
          // contacts, which never carry isGroup.
          if (updatedContact.isGroup && !isCurrentlyClosed) {
            if (!state.data.Groups) state.data.Groups = [];
            const groupsIdx = state.data.Groups.findIndex(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (groupsIdx !== -1) {
              state.data.Groups[groupsIdx] = updatedContact;
            } else {
              state.data.Groups.unshift(updatedContact);
            }
          }

          // Determine if contact is assigned (has assignedTo and it's not "Unassigned")
          // Groups never have assignment semantics (WhatsApp Groups plan decision #2) -- force
          // both false so a group's live update never leaks into the Assigned/Unassigned tabs.
          const assignedTo = updatedContact.assignedTo || updatedContact.lastMessage?.assignedTo;
          const isAssigned = !updatedContact.isGroup && assignedTo && assignedTo !== "Unassigned" && updatedContact.status !== "Unassigned";
          // Determine if contact is unassigned (no assignedTo or assignedTo is "Unassigned" or status is "Unassigned")
          const isUnassigned = !updatedContact.isGroup && (!assignedTo || assignedTo === "Unassigned" || updatedContact.status === "Unassigned");
          
          // --- Update 'Assigned' category ---
          // Only add to Assigned if NOT closed and IS assigned
          if (!isCurrentlyClosed && isAssigned) {
            if (!state.data.Assigned) state.data.Assigned = [];
            const assignedIdx = state.data.Assigned.findIndex(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (assignedIdx === -1) {
              state.data.Assigned.unshift(updatedContact);
            } else {
              state.data.Assigned[assignedIdx] = updatedContact;
            }
          } else {
            // Remove from Assigned category if not assigned OR if closed
            if (state.data.Assigned) {
              state.data.Assigned = state.data.Assigned.filter(c => {
                const cId = c?.contactId || c?.contactID;
                return !cId || String(cId) !== contactIdStr;
              });
            }
          }
          
          // --- Update 'Unassigned' category ---
          // Only add to Unassigned if NOT closed and IS unassigned
          if (!isCurrentlyClosed && isUnassigned) {
            if (!state.data.Unassigned) state.data.Unassigned = [];
            const unassignedIndex = state.data.Unassigned.findIndex(c => {
              const cId = c?.contactId || c?.contactID;
              return cId && String(cId) === contactIdStr;
            });
            if (unassignedIndex !== -1) {
              state.data.Unassigned[unassignedIndex] = updatedContact;
            } else {
              state.data.Unassigned.unshift(updatedContact);
            }
          } else {
            // Remove from Unassigned if assigned OR if closed
            if (state.data.Unassigned) {
              state.data.Unassigned = state.data.Unassigned.filter(c => {
                const cId = c?.contactId || c?.contactID;
                return !cId || String(cId) !== contactIdStr;
              });
            }
          }
          
          // --- Update 'Active' category ---
          // Active: contacts where conversation has started and less than 24 hours have passed (activeStatus > 0)
          // This applies regardless of assignment status (assigned/unassigned/closed)
          if (!state.data.Active) state.data.Active = [];
          const activeIndex = state.data.Active.findIndex(c => {
            const cId = c?.contactId || c?.contactID;
            return cId && String(cId) === contactIdStr;
          });
          
          if (updatedContact.activeStatus > 0) {
            // Contact is active (conversation started and < 24 hours)
            if (activeIndex !== -1) {
              state.data.Active[activeIndex] = updatedContact;
            } else {
              state.data.Active.unshift(updatedContact);
            }
          } else {
            // Contact is not active (no conversation or > 24 hours), remove from Active
            if (activeIndex !== -1) {
              state.data.Active.splice(activeIndex, 1);
            }
          }

          // --- Update 'You' category for regular update ---
          const isYou = (currentUserId && assignedTo === currentUserId) || (activeMemberIds.length > 0 && activeMemberIds.includes(assignedTo));
          if (isYou) {
            if (!state.data.You) state.data.You = [];
            const youIdx = state.data.You.findIndex(c => c.contactId === contactId);
            if (youIdx === -1) {
              state.data.You.unshift(updatedContact);
            } else {
              state.data.You[youIdx] = {
                ...state.data.You[youIdx],
                ...updatedContact
              };
            }
          } else {
            if (state.data.You) {
              state.data.You = state.data.You.filter(c => c.contactId !== contactId);
            }
          }
        }

      } else if (closeStatus) {
        // Contact not found, but it's a close action. Add it to the correct closed category only
        const closeCategory = closeStatus.charAt(0).toUpperCase() + closeStatus.slice(1);
        const newContact = {
          ...contactData,
          status: closeCategory,
          closeStatus: closeCategory,
          // Ensure closed contacts do NOT carry assignment
          assignedTo: null,
          assignedToName: null,
          // Preserve activeStatus from contactData (don't set to 0 at runtime - let server handle it on refresh)
          activeStatus: contactData.activeStatus ?? contactData.hoursRemaining ?? contactData.hoursLeft ?? 0,
          lastMessage: {
            ...(contactData.lastMessage || {}),
            assignedTo: null,
            assignedToName: null,
          }
        };

        // SIMPLE: Remove from arrays (All, You, Unassigned, Assigned, TeamMembers, Teams, and other closed category)
        // Active is handled separately - only remove if activeStatus is 0
        removeContactFromAllArrays(state, contactIdStr, newContact.activeStatus);

        // Add ONLY to the appropriate closed category
        if (!state.data[closeCategory]) state.data[closeCategory] = [];
        const existingInClosed = state.data[closeCategory].findIndex(c => c && c.contactId === contactId);
        if (existingInClosed !== -1) {
          state.data[closeCategory][existingInClosed] = newContact;
        } else {
          state.data[closeCategory].unshift(newContact);
          bumpWithheldCategoryTotal(state, closeCategory, contactIdStr);
        }

        // Update Active array based on activeStatus
        if (!state.data.Active) state.data.Active = [];
        const activeIndex = state.data.Active.findIndex(c => c && c.contactId === contactId);
        if (newContact.activeStatus > 0) {
          // Keep in Active if activeStatus > 0
          if (activeIndex !== -1) {
            state.data.Active[activeIndex] = newContact;
          } else {
            state.data.Active.unshift(newContact);
          }
        } else {
          // Remove from Active if activeStatus is 0
          if (activeIndex !== -1) {
            state.data.Active.splice(activeIndex, 1);
          }
        }

        // CRITICAL: Double-check and ensure contact is NOT in All array (safety check)
        if (state.data.All && Array.isArray(state.data.All)) {
          const contactIdStr = String(contactId);
          const allIndex = state.data.All.findIndex(c => c && c.contactId && String(c.contactId) === contactIdStr);
          if (allIndex !== -1) {
            state.data.All.splice(allIndex, 1);
          }
        }

      } else {
         // This is a new contact, not a close action.
         // Default to adding to 'All' and 'Unassigned'
          const calculatedActiveStatus = contactData.activeStatus !== undefined 
            ? contactData.activeStatus 
            : (contactData.hoursRemaining || contactData.hoursLeft || 0);
            
          // Groups never have assignment semantics (WhatsApp Groups plan decision #2) --
          // a group's status/assignedTo must never default to "Unassigned" the way a real
          // 1:1 contact's does, or its first-ever live message would incorrectly leak into
          // the Unassigned tab/count.
          // Also recognised by the group-only chat types a group's live event carries
          // (group_created, group_participants_update, ...): a brand-new group's first live update
          // must never be filed as a default "Unassigned" 1:1 contact, even if the payload's
          // isGroup flag didn't survive the trip (seen live: a just-added group showed an
          // Unassigned chip in All and never reached the Groups tab).
          const isNewGroup =
            contactData.isGroup === true ||
            !!contactData.groupId ||
            String(contactData.lastMessage?.type || contactData.chat?.type || "").startsWith("group_");
          const newContact = {
            contactId,
            name: contactData.name || "Unknown",
            status: isNewGroup ? "All" : (contactData.status || "Unassigned"),
            assignedTo: isNewGroup ? null : (contactData.chat?.type === "assignment" ? contactData.chat.to : contactData.assignedTo),
            assignedToName: isNewGroup ? null : (contactData.chat?.type === "assignment" ? contactData.chat.toName : contactData.assignedToName),
            lastMessage: contactData.lastMessage || "",
            activeStatus: calculatedActiveStatus, // Ensure activeStatus is set
            hoursRemaining: contactData.hoursRemaining || contactData.hoursLeft || 0,
            receivedCount: contactData.receivedCount || 0,
            chat: contactData.chat || null,
            platform: contactData.platform || "whatsapp", // For correct icon in recent list (whatsapp | qr | instagram | facebook)
            // The QR number this chat is on: lets the card show its number tag from the very first live message (this
            // card is built field by field, unlike the existing-contact path below which spreads the whole payload).
            ...(contactData.connectionId ? { connectionId: contactData.connectionId } : {}),
            ...(isNewGroup && { isGroup: true, groupId: contactData.groupId || contactId }),
          };

        // CRITICAL: Check if contact is closed before adding to All
        const isCurrentlyClosed = state.data.Closed?.some(c => {
          const cId = c?.contactId || c?.contactID;
          return cId && String(cId) === contactIdStr;
        });

        // Only add to All if NOT closed
        if (!isCurrentlyClosed) {
          if (!state.data.All) state.data.All = [];
          state.data.All.unshift(newContact);
        }

        // --- Sync into 'Groups' category (WhatsApp Groups plan) --- a brand-new group's
        // very first-ever live message must also seed the Groups tab, not just 'All'.
        if (isNewGroup && !isCurrentlyClosed) {
          if (!state.data.Groups) state.data.Groups = [];
          state.data.Groups.unshift(newContact);
        }

        if (newContact.status === "Unassigned") {
          if (!state.data.Unassigned) state.data.Unassigned = [];
          state.data.Unassigned.unshift(newContact);
        }

        // Add to Active array if activeStatus > 0 (conversation started and < 24 hours)
        if (newContact.activeStatus > 0) {
          if (!state.data.Active) state.data.Active = [];
          state.data.Active.unshift(newContact);
        }

        // --- Update 'You' category for new contact ---
        const assignedTo = newContact.assignedTo || newContact.lastMessage?.assignedTo;
        const isYou = (currentUserId && assignedTo === currentUserId) || (activeMemberIds.length > 0 && activeMemberIds.includes(assignedTo));
        if (isYou) {
          if (!state.data.You) state.data.You = [];
          const youIdx = state.data.You.findIndex(c => c.contactId === contactId);
          if (youIdx === -1) {
            state.data.You.unshift(newContact);
          } else {
            state.data.You[youIdx] = {
              ...state.data.You[youIdx],
              ...newContact
            };
          }
        } else {
          if (state.data.You) {
            state.data.You = state.data.You.filter(c => c.contactId !== contactId);
          }
        }

        // Update team assignment for new contact
        const newAssignedTo = newContact.assignedTo || newContact.lastMessage?.assignedTo;
        if (newAssignedTo && newAssignedTo !== "Unassigned") {
          updateTeamAssignment(state, contactId, newAssignedTo, null, newContact);
        }
      }

      // Recalculate all counts including team counts
      updateAllCounts(state);

      // For media messages, set lastMessage.content to the type
      const mediaTypes = ["image", "audio", "video", "document","template","catalog","catalog_purchase"];
      const productOrderPreview = "🛒 Product Order";
      if (
        contactData.lastMessage &&
        mediaTypes.includes(contactData.lastMessage.type)
      ) {
        contactData.lastMessage.content =
          contactData.lastMessage.type === "catalog" || contactData.lastMessage.type === "catalog_purchase"
            ? productOrderPreview
            : contactData.lastMessage.type.toUpperCase();
      } else if (
        contactData.chat &&
        mediaTypes.includes(contactData.chat.type)
      ) {
        if (!contactData.lastMessage) contactData.lastMessage = {};
        contactData.lastMessage.content =
          contactData.chat.type === "catalog" || contactData.chat.type === "catalog_purchase"
            ? productOrderPreview
            : contactData.chat.type.toUpperCase();
        contactData.lastMessage.type = contactData.chat.type;
        contactData.lastMessage.timestamp = ensureSerializableTimestamp(contactData.chat.timestamp);
      } else if (
        contactData.lastMessage &&
        (contactData.lastMessage.messageType === "catalog" ||
          contactData.lastMessage.messageType === "catalog_purchase") &&
        (contactData.lastMessage.type === "chat" || !contactData.lastMessage.type)
      ) {
        contactData.lastMessage.content = productOrderPreview;
        contactData.lastMessage.type = contactData.lastMessage.messageType;
      } else if (contactData.lastMessage && !contactData.lastMessage.content) {
        // CRITICAL: For text messages (including internal mentions), ensure content is set from body/message
        // This fixes the issue where message text shows as "chat" after refresh
        const messageText = contactData.lastMessage.body || 
                             contactData.lastMessage.message || 
                             contactData.chat?.body || 
                             contactData.chat?.message || 
                             "";
        if (messageText) {
          contactData.lastMessage.content = messageText;
        }
        // CRITICAL: Ensure messageType is set to "text" for text messages so MessageCard displays content correctly
        if (!contactData.lastMessage.messageType && 
            (contactData.lastMessage.type === "chat" || contactData.lastMessage.type === "text")) {
          contactData.lastMessage.messageType = "text";
        }
      }

      // FINAL SAFEGUARD: Ensure closed contacts are NEVER in All array
      // This runs after all updates to catch any edge cases
      if (state.data && state.data.All && Array.isArray(state.data.All)) {
        const closedContactIds = new Set();
        
        // Collect all closed contact IDs
        ["Closed"].forEach(category => {
          if (state.data[category] && Array.isArray(state.data[category])) {
            state.data[category].forEach(contact => {
              if (contact) {
                const cId = contact.contactId || contact.contactID;
                if (cId) {
                  closedContactIds.add(String(cId));
                }
              }
            });
          }
        });
        
        // Remove any closed contacts from All array
        if (closedContactIds.size > 0) {
          state.data.All = state.data.All.filter(contact => {
            if (!contact) return true;
            const cId = contact.contactId || contact.contactID;
            if (!cId) return true;
            return !closedContactIds.has(String(cId));
          });
        }
      }

      return;
      },
      prepare(contactData) {
        // Resolve the current user's id here, BEFORE dispatch — store.getState() is only
        // safe to call outside of an in-flight reducer. See the note in reducer() above.
        let currentUserId = null;
        try {
          if (typeof window !== 'undefined' && window.store?.getState) {
            currentUserId = window.store.getState()?.auth?.user?._id || null;
          }
        } catch (e) { /* ignore */ }
        return { payload: contactData, meta: { currentUserId } };
      },
    },

    clearSearchResults(state) {
      state.searchResults = null;
      state.isSearching = false;
    },
    clearUnreadCount(state, action) {
      const contactId = action.payload;

      if (!contactId) {
        return;
      }
      
      // Clear unread count in all categories where this contact exists
      if (state.data && typeof state.data === 'object') {
        Object.keys(state.data).forEach(category => {
          const categoryData = state.data[category];
          if (categoryData && Array.isArray(categoryData)) {
            const existingIndex = categoryData.findIndex(
              contact => {
                if (!contact) return false;
                const cId = contact.contactId || contact.contactID;
                return cId && String(cId) === String(contactId);
              }
            );
            
            if (existingIndex !== -1) {
              state.data[category][existingIndex].receivedCount = 0;
              state.data[category][existingIndex].unreadCount = 0;
              state.data[category][existingIndex].markStatus = "read";
              // Also clear the last-message-unread flag — inboxContactMatchesUnreadFilter
              // (Unread quick-filter, tab badge) keys off this, not receivedCount/unreadCount,
              // so leaving it stale meant both stayed wrong until the next full inbox refetch.
              state.data[category][existingIndex].lastMessageUnreadByTeam = false;
            }
          }
        });
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setConnectionFilter.pending, (state, action) => {
        state.selectedConnectionId = action.meta.arg || null;
        state.loading = true;
      })
      .addCase(setConnectionFilter.fulfilled, (state, action) => {
        // Drop every lazily-loaded bucket: they were fetched under the previous filter.
        ["Closed", "Blocked", "Groups"].forEach((k) => {
          state.data[k] = [];
          if (state.lazyTabsLoaded) state.lazyTabsLoaded[k] = false;
        });
        state.lifecycleByStage = {};
        if (state.agentByAgentId) state.agentByAgentId = {};
        state.loading = false;
        state.initialized = true;
        applyInboxPayload(state, action.payload.data);
      })
      .addCase(setConnectionFilter.rejected, (state, action) => {
        // Back to "All numbers": keep the module-level filter and the UI in agreement.
        activeConnectionFilter = null;
        state.selectedConnectionId = null;
        state.loading = false;
        state.error = action.payload || "Failed to filter inbox";
      })
      .addCase(fetchInbox.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInbox.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        applyInboxPayload(state, action.payload);
      })
      .addCase(fetchInbox.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.initialized = true;
          state.error = action.payload || "Something went wrong";
        }
      })
      // Background refresh (stale-while-revalidate) — no loading state changes
      .addCase(refreshInboxBackground.fulfilled, (state, action) => {
        if (!action.payload) return;
        // Always fetched with skipClosed:true, so Closed never carries
        // real data here — fetchClosedTabInbox remains the sole source for lazyTabsLoaded.
        applyInboxPayload(state, action.payload);
      })
      .addCase(refreshInboxBackground.pending, () => { /* no loading spinner */ })
      .addCase(refreshInboxBackground.rejected, () => { /* silent failure */ })
      // Load-more handlers
      .addCase(loadMoreInbox.pending, (state) => {
        state.isLoadingMore = true;
      })
      .addCase(loadMoreInbox.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        const { payload, category, lifecycleStageId, agentId, page, limit } = action.payload || {};
        if (!payload) return;

        if (lifecycleStageId) {
          const bucket = state.lifecycleByStage[lifecycleStageId];
          if (!bucket) return;
          const categoryPayload = payload.Lifecycle;
          if (!categoryPayload || !Array.isArray(categoryPayload.data)) return;
          const newItems = convertMessagesTimestamps(categoryPayload.data);
          const existingIds = new Set(bucket.data.map(c => String(c.contactId || c.contactID)));
          const uniqueNew = newItems.filter(c => !existingIds.has(String(c.contactId || c.contactID)));
          bucket.data = [...bucket.data, ...uniqueNew];
          bucket.page = page;
          bucket.limit = limit;
          bucket.hasMore = categoryPayload.hasMore ?? false;
          bucket.count = categoryPayload.count ?? bucket.count;
          return;
        }

        if (agentId) {
          const bucket = state.agentByAgentId[agentId];
          if (!bucket) return;
          const categoryPayload = payload.Agent;
          if (!categoryPayload || !Array.isArray(categoryPayload.data)) return;
          const newItems = convertMessagesTimestamps(categoryPayload.data);
          const existingIds = new Set(bucket.data.map(c => String(c.contactId || c.contactID)));
          const uniqueNew = newItems.filter(c => !existingIds.has(String(c.contactId || c.contactID)));
          bucket.data = [...bucket.data, ...uniqueNew];
          bucket.page = page;
          bucket.limit = limit;
          bucket.hasMore = categoryPayload.hasMore ?? false;
          bucket.count = categoryPayload.count ?? bucket.count;
          return;
        }

        if (!category || !state.data[category]) return;

        const categoryPayload = payload[category];
        if (!categoryPayload || !Array.isArray(categoryPayload.data)) return;

        const newItems = convertMessagesTimestamps(categoryPayload.data);
        // Append, deduplicating by contactId
        const existingIds = new Set(
          (state.data[category] || []).map(c => String(c.contactId || c.contactID))
        );
        const uniqueNew = newItems.filter(c => !existingIds.has(String(c.contactId || c.contactID)));
        state.data[category] = [...(state.data[category] || []), ...uniqueNew];

        if (state.pagination?.[category]) {
          state.pagination[category] = {
            page,
            limit,
            hasMore:    categoryPayload.hasMore ?? false,
            totalCount: categoryPayload.count   ?? state.pagination[category].totalCount,
          };
        }
      })
      .addCase(loadMoreInbox.rejected, (state) => {
        state.isLoadingMore = false;
      })
      .addCase(fetchClosedTabInbox.fulfilled, (state, action) => {
        const payload = action.payload;
        if (!payload) return;
        ['Closed' ,'Blocked', 'Groups'].forEach(key => {
          if (payload[key] && Array.isArray(payload[key].data)) {
            let convertedData = convertMessagesTimestamps(payload[key].data);
            if (convertedData.length > MAX_CONTACTS_PER_CATEGORY) {
              convertedData = convertedData.slice(0, MAX_CONTACTS_PER_CATEGORY);
            }
            state.data[key] = convertedData;
            // Groups' badge is unread messages, not "how many groups" (see sumGroupsUnread's own
            // comment) -- pagination.totalCount below still uses payload[key].count (the real
            // group total) unchanged, since that's what "load more" needs.
            state.counts[key] = key === 'Groups' ? sumGroupsUnread(convertedData) : (payload[key].count ?? convertedData.length);
            if (state.pagination?.[key] !== undefined) {
              state.pagination[key] = {
                page: payload[key].page ?? 1,
                limit: payload[key].limit ?? 50,
                hasMore: payload[key].hasMore ?? false,
                totalCount: payload[key].count ?? convertedData.length,
              };
            }
          }
        });
        const requestedCategory = action.meta.arg;
        if (requestedCategory === 'Closed'|| requestedCategory === 'Blocked' || requestedCategory === 'Groups') {

          state.lazyTabsLoaded[requestedCategory] = true;
        }
      })
      .addCase(fetchClosedTabInbox.rejected, () => {
        // Silently ignore — debounce / already-loaded rejections are normal
      })
      .addCase(fetchLifecycleTabInbox.pending, (state) => {
        state.lifecycleTabLoading = true;
      })
      .addCase(fetchLifecycleTabInbox.fulfilled, (state, action) => {
        state.lifecycleTabLoading = false;
        const arg = action.meta.arg;
        const stageId = typeof arg === "string" ? arg : arg?.stageId;
        if (!stageId) return;
        state.selectedLifecycleStageId = stageId;
        const payload = action.payload?.Lifecycle;
        if (!payload || !Array.isArray(payload.data)) {
          state.lifecycleByStage[stageId] = { data: [], count: 0, page: 1, limit: 50, hasMore: false, loadedAt: Date.now() };
          return;
        }
        const convertedData = convertMessagesTimestamps(payload.data);
        state.lifecycleByStage[stageId] = {
          data: convertedData,
          count: payload.count ?? convertedData.length,
          page: payload.page ?? 1,
          limit: payload.limit ?? 50,
          hasMore: payload.hasMore ?? false,
          loadedAt: Date.now(),
        };
      })
      .addCase(fetchLifecycleTabInbox.rejected, (state, action) => {
        state.lifecycleTabLoading = false;
        // "Already cached" is the normal cache-hit path (see the thunk above), not an
        // error — still record which stage is now selected so the UI/live-patch logic
        // has the right stageId even though no real fetch happened this time.
        const arg = action.meta.arg;
        const stageId = typeof arg === "string" ? arg : arg?.stageId;
        if (stageId && action.payload === "Already cached") {
          state.selectedLifecycleStageId = stageId;
        }
        // Otherwise silently ignore, same as fetchClosedTabInbox.rejected — a stale/
        // cancelled fetch (e.g. a fast double-click between two stages) isn't a real error.
      })
      .addCase(fetchAgentTabInbox.pending, (state) => {
        state.agentTabLoading = true;
      })
      .addCase(fetchAgentTabInbox.fulfilled, (state, action) => {
        state.agentTabLoading = false;
        const arg = action.meta.arg;
        const agentId = typeof arg === "string" ? arg : arg?.agentId;
        if (!agentId) return;
        state.selectedAgentId = agentId;
        const payload = action.payload?.Agent;
        if (!payload || !Array.isArray(payload.data)) {
          state.agentByAgentId[agentId] = { data: [], count: 0, page: 1, limit: 50, hasMore: false, loadedAt: Date.now() };
          return;
        }
        const convertedData = convertMessagesTimestamps(payload.data);
        state.agentByAgentId[agentId] = {
          data: convertedData,
          count: payload.count ?? convertedData.length,
          page: payload.page ?? 1,
          limit: payload.limit ?? 50,
          hasMore: payload.hasMore ?? false,
          loadedAt: Date.now(),
        };
      })
      .addCase(fetchAgentTabInbox.rejected, (state, action) => {
        state.agentTabLoading = false;
        // "Already cached" is the normal cache-hit path (see the thunk above), not an
        // error — still record which agent is now selected so the UI/live-patch logic
        // has the right agentId even though no real fetch happened this time.
        const arg = action.meta.arg;
        const agentId = typeof arg === "string" ? arg : arg?.agentId;
        if (agentId && action.payload === "Already cached") {
          state.selectedAgentId = agentId;
        }
        // Otherwise silently ignore, same as fetchLifecycleTabInbox.rejected.
      })
      .addCase(searchInbox.pending, (state) => {
        state.isSearching = true;
        // Drop the previous search's results immediately, not just on
        // success -- otherwise searching a second term while one is already
        // showing would flash the OLD term's results during the loading
        // state before the new ones arrive (2026-09-08, same "no flicker,
        // wait for the real DB call" principle as the frontend change).
        state.searchResults = null;
      })
      .addCase(searchInbox.fulfilled, (state, action) => {
        state.isSearching = false;
        const payload = action.payload;
        if (!payload) return;
        // Flatten All + Closed into a single deduplicated list
        const seen = new Set();
        const results = [];
        // 'Groups' is only populated for a Groups-tab search (otherwise it is an empty placeholder).
        ['All', 'Closed', 'Groups'].forEach(key => {
          if (payload[key] && Array.isArray(payload[key].data)) {
            convertMessagesTimestamps(payload[key].data).forEach(c => {
              const id = String(c.contactId || c.contactID || '');
              if (id && !seen.has(id)) { seen.add(id); results.push(c); }
            });
          }
        });
        state.searchResults = results;
      })
      .addCase(searchInbox.rejected, (state) => {
        state.isSearching = false;
      })
      .addCase(markMessageAsRead.fulfilled, (state, action) => {
        const p = action.payload;
        if (!p || p.success === false) return;
        const rawId = action.meta?.arg?.contactId;
        if (!rawId) return;
        const contactId = String(rawId);
        if (!state.data || typeof state.data !== "object") return;
        Object.keys(state.data).forEach((category) => {
          const categoryData = state.data[category];
          if (!Array.isArray(categoryData)) return;
          categoryData.forEach((contact, idx) => {
            if (!contact) return;
            const cId = contact.contactId || contact.contactID;
            if (cId && String(cId) === contactId) {
              state.data[category][idx].receivedCount = 0;
              // See clearUnreadCount above — same stale-flag issue.
              state.data[category][idx].lastMessageUnreadByTeam = false;
            }
          });
        });
      })
      // Listen to removeContactFromAssignment action from inboxSettingsSlice
      .addCase(removeContactFromAssignment, (state, action) => {
        const { contactId, assignedToId, assignedToType } = action.payload;
        
        if (!contactId || !assignedToId || !assignedToType) {
          return; // Invalid payload, skip update
        }

        // Initialize arrays if they don't exist
        if (!state.data.TeamMembers) state.data.TeamMembers = [];
        if (!state.data.Teams) state.data.Teams = [];

        if (assignedToType === 'teammember') {
          // Remove contact from team member's data array
          const teamMemberIndex = state.data.TeamMembers.findIndex(
            member => (member._id === assignedToId || member.id === assignedToId)
          );
          
          if (teamMemberIndex !== -1) {
            const teamMember = state.data.TeamMembers[teamMemberIndex];
            
            if (Array.isArray(teamMember.data)) {
              teamMember.data = teamMember.data.filter(
                (contact) => contact.contactId !== contactId
              );
            } else {
              teamMember.data = [];
            }
            // Update count to 0 if no contacts, but keep the team member
            teamMember.count = teamMember.data.length;
          }
        } else if (assignedToType === 'team') {
          // Remove contact from team's data array
          const teamIndex = state.data.Teams.findIndex(
            team => (team._id === assignedToId || team.id === assignedToId)
          );
          
          if (teamIndex !== -1) {
            const team = state.data.Teams[teamIndex];
            
            if (Array.isArray(team.data)) {
              team.data = team.data.filter(
                (contact) => contact.contactId !== contactId
              );
            } else {
              team.data = [];
            }
            // Update count to 0 if no contacts, but keep the team
            team.count = team.data.length;
          }
        }
      })
      // Listen to addContactToAssignment action from inboxSettingsSlice.
      // Only patches the member/team's array if it's already loaded (i.e. found
      // in state.data.TeamMembers/Teams) — if the sidebar list hasn't been
      // fetched yet, the next fetchInboxTeamData call will bring it in correctly.
      .addCase(addContactToAssignment, (state, action) => {
        const { contactId, assignedToId, assignedToType } = action.payload;

        if (!contactId || !assignedToId || !assignedToType) {
          return; // Invalid payload, skip update
        }

        if (!state.data.TeamMembers) state.data.TeamMembers = [];
        if (!state.data.Teams) state.data.Teams = [];

        if (assignedToType === 'teammember') {
          const teamMemberIndex = state.data.TeamMembers.findIndex(
            member => (member._id === assignedToId || member.id === assignedToId)
          );

          if (teamMemberIndex !== -1) {
            const teamMember = state.data.TeamMembers[teamMemberIndex];

            if (!Array.isArray(teamMember.data)) teamMember.data = [];
            const alreadyPresent = teamMember.data.some(
              (contact) => contact.contactId === contactId || contact.contactID === contactId
            );
            if (!alreadyPresent) {
              teamMember.data.unshift({ contactId, contactID: contactId });
            }
            teamMember.count = teamMember.data.length;
          }
        } else if (assignedToType === 'team') {
          const teamIndex = state.data.Teams.findIndex(
            team => (team._id === assignedToId || team.id === assignedToId)
          );

          if (teamIndex !== -1) {
            const team = state.data.Teams[teamIndex];

            if (!Array.isArray(team.data)) team.data = [];
            const alreadyPresent = team.data.some(
              (contact) => contact.contactId === contactId || contact.contactID === contactId
            );
            if (!alreadyPresent) {
              team.data.unshift({ contactId, contactID: contactId });
            }
            team.count = team.data.length;
          }
        }
      });
  },
});

export const { addMessageCard, removeContactFromInbox, removeGroupFromInbox, clearUnreadCount, updateAssignmentInInbox, updateContactNameInInbox, updateLifecycleStageInInbox, clearSearchResults, removeContactFromLifecycleList, invalidateLifecycleStageCache } = inboxRecentSlice.actions;
export default inboxRecentSlice.reducer;

// Helper to calculate inbox counts for all categories
export const calculateAndDispatchInboxCounts = (dispatch, data) => {
  const counts = {};
  // Standard counts
  Object.keys(data).forEach(category => {
    counts[category] = data[category].length;
  });

  // Calculate team member counts
  if (data.TeamMembers && Array.isArray(data.TeamMembers)) {
    counts.teamMemberCounts = {};
    data.TeamMembers.forEach(member => {
      // Prefer .count if available, else fallback to .data.length
      counts.teamMemberCounts[member._id] = typeof member.count === 'number'
        ? member.count
        : (Array.isArray(member.data) ? member.data.length : 0);
    });
  }

  // Calculate team counts
  if (data.Teams && Array.isArray(data.Teams)) {
    counts.teamCounts = {};
    data.Teams.forEach(team => {
      counts.teamCounts[team._id] = typeof team.count === 'number'
        ? team.count
        : (Array.isArray(team.data) ? team.data.length : 0);
    });
  }

  dispatch(setInboxCounts(counts));
};

// Helper function to calculate team member and team counts
const calculateTeamCounts = (state) => {
  const teamMemberCounts = {};
  const teamCounts = {};

  // Get all contacts from all categories
  const allContacts = [];
  if (state.data && typeof state.data === 'object') {
    Object.keys(state.data).forEach(category => {
      const categoryData = state.data[category];
      if (categoryData && Array.isArray(categoryData)) {
        allContacts.push(...categoryData);
      }
    });
  }

  // Calculate team member counts
  allContacts.forEach(contact => {
    const assignedTo = contact.assignedTo || contact.lastMessage?.assignedTo;
    if (assignedTo && assignedTo !== "Unassigned") {
      teamMemberCounts[assignedTo] = (teamMemberCounts[assignedTo] || 0) + 1;
    }
  });

  // Calculate team counts (assuming teams are stored in state.data.Teams)
  if (state.data.Teams && Array.isArray(state.data.Teams)) {
    state.data.Teams.forEach(team => {
      const teamId = team._id || team.id;
      if (teamId) {
        // Count contacts assigned to this team's members
        let teamContactCount = 0;
        allContacts.forEach(contact => {
          const assignedTo = contact.assignedTo || contact.lastMessage?.assignedTo;
          if (assignedTo && team.members && Array.isArray(team.members)) {
            const isTeamMember = team.members.some(member => 
              member._id === assignedTo || member.id === assignedTo
            );
            if (isTeamMember) {
              teamContactCount++;
            }
          }
        });
        teamCounts[teamId] = teamContactCount;
      }
    });
  }

  return { teamMemberCounts, teamCounts };
};

// Bug fix (2026-09-12) — bumps the real Closed/Blocked total by exactly 1 the
// instant a genuinely NEW contact lands in that category live. Needed
// because updateAllCounts (below) deliberately SKIPS recalculating
// Closed/Blocked until state.lazyTabsLoaded[category] is true (its own
// comment: recomputing from the still-mostly-empty withheld array would
// clobber the accurate backend total back down) — which, before the tab is
// ever opened, means nothing else ever advances the total when a live close
// arrives, and the Closed badge freezes at its initial snapshot forever.
// Call this at the exact point of the push instead of relying on
// updateAllCounts to notice, so it works whether or not the tab has been
// opened yet.
//
// Made idempotent per contact (2026-09-12, found live: closing one contact
// jumped the total by 2, not 1) — the Close button dispatches closeContact
// (contactsSlice.js), a completely separate slice from this one; this file's
// 3 near-identical "add to Closed" reducers appear to also independently fire
// for the same real close (most likely a live socket echo of the same event
// back to the same client). Rather than chase exactly which second dispatch
// path fires, track which contactIds have already been counted this session
// so a genuine duplicate can never double-bump the total, regardless of how
// many separate code paths process the same underlying close.
const bumpWithheldCategoryTotal = (state, category, contactIdStr) => {
  if (!state._withheldTotalBumpedIds) state._withheldTotalBumpedIds = {};
  if (!state._withheldTotalBumpedIds[category]) state._withheldTotalBumpedIds[category] = {};
  if (contactIdStr && state._withheldTotalBumpedIds[category][contactIdStr]) {
    return; // already counted this contact for this category this session
  }
  if (contactIdStr) state._withheldTotalBumpedIds[category][contactIdStr] = true;

  if (!state.pagination) state.pagination = {};
  if (!state.pagination[category]) {
    state.pagination[category] = { hasMore: true, totalCount: 0, page: 1, limit: 50, baseLen: 0 };
  }
  state.pagination[category].totalCount = (state.pagination[category].totalCount ?? 0) + 1;
  if (!state.counts) state.counts = {};
  state.counts[category] = state.pagination[category].totalCount;
};

// Groups tab badge (bug fix, 2026-09-26 follow-up): unlike every other category (whose badge is
// "how many conversations"), the Groups badge should show total UNREAD MESSAGES across every
// group, matching what inboxController.js's own groupsUnreadCount now computes server-side.
// Each hydrated group card already carries its own `receivedCount` (hydrateGroupsNeeded's own
// per-group unread field, same one Groups/All list rows already read) -- summing it here at the
// two points state.counts.Groups gets set client-side keeps this consistent with the server value
// instead of falling back to array length once the tab has been opened locally.
const sumGroupsUnread = (arr) =>
  (Array.isArray(arr) ? arr : []).reduce((sum, g) => sum + (Number(g?.receivedCount) || 0), 0);

// Helper function to update all counts including team counts
const updateAllCounts = (state) => {
  if (!state.counts) state.counts = {};

  if (state.data && typeof state.data === 'object') {
    Object.keys(state.data).forEach(category => {
      // Closed starts "withheld" (skipClosed lazy-load sentinel — see
      // applyInboxPayload): state.data[category] stays an empty array until the tab is
      // opened (fetchClosedTabInbox), even though state.counts[category] already holds
      // the accurate backend total. Recomputing from the still-empty array here would
      // clobber that accurate count back to 0 on every socket event that runs
      // updateAllCounts before the tab is ever opened — leave it untouched until then.

      if ((category === 'Closed' || category === 'Blocked' || category === 'Groups') && !state.lazyTabsLoaded?.[category]) {
        return;
      }

      const categoryData = state.data[category];
      const dataLength = (categoryData && Array.isArray(categoryData)) ? categoryData.length : 0;
      const hasMore = state.pagination?.[category]?.hasMore ?? false;
      if (hasMore) {
        // Server has more pages. Adjust the stored totalCount by the delta since the
        // last server response (baseLen) so socket-event adds and removes are reflected.
        const prevTotal = state.pagination[category].totalCount ?? 0;
        const baseLen = state.pagination[category].baseLen ?? (state.pagination[category].limit ?? 50);
        const delta = dataLength - baseLen;
        const adjusted = Math.max(0, prevTotal + delta);
        // pagination.totalCount always tracks the real conversation/group total (what "load
        // more" paginates against) — only the visible badge (state.counts) differs for Groups.
        state.pagination[category].totalCount = Math.max(adjusted, dataLength);
        state.pagination[category].baseLen = dataLength; // advance baseline for the next call
        state.counts[category] = category === 'Groups' ? sumGroupsUnread(categoryData) : Math.max(adjusted, dataLength);
      } else {
        // All contacts locally loaded — array length is exact (except Groups, whose badge is
        // unread messages, not conversation count — see sumGroupsUnread's own comment above).
        state.counts[category] = category === 'Groups' ? sumGroupsUnread(categoryData) : dataLength;
        if (state.pagination?.[category]) {
          state.pagination[category].totalCount = dataLength;
        }
      }
    });
  }

  // Update team member and team counts
  const { teamMemberCounts, teamCounts } = calculateTeamCounts(state);
  state.counts.teamMemberCounts = teamMemberCounts;
  state.counts.teamCounts = teamCounts;
};

// Simple function to remove contact from all arrays when closed
// Note: Active array is handled separately based on activeStatus
const removeContactFromAllArrays = (state, contactId, activeStatus = 0) => {
  if (!contactId) return; // Safety check
  
  // Normalize contactId to string for reliable comparison
  const contactIdStr = String(contactId);
  
  // CRITICAL: Remove from All array FIRST and explicitly
  // Handle both contactId and contactID fields, use string comparison
  if (state.data.All && Array.isArray(state.data.All)) {
    // Use multiple removal strategies for maximum reliability
    // Strategy 1: Filter approach
    const beforeLength = state.data.All.length;
    state.data.All = state.data.All.filter(
      contact => {
        if (!contact) return true; // Keep if no contact
        // Check both contactId and contactID fields
        const cId = contact.contactId || contact.contactID;
        if (!cId) return true; // Keep if no ID
        // Compare as strings to handle number/string mismatches
        const contactIdMatch = String(cId) === contactIdStr;
        return !contactIdMatch; // Remove if matches
      }
    );
    
    // Strategy 2: If filter didn't remove anything, try reverse iteration with splice
    if (state.data.All.length === beforeLength) {
      // Filter didn't remove anything, try finding and removing manually (reverse iteration for safety)
      for (let i = state.data.All.length - 1; i >= 0; i--) {
        const contact = state.data.All[i];
        if (contact) {
          const cId = contact.contactId || contact.contactID;
          if (cId && String(cId) === contactIdStr) {
            state.data.All.splice(i, 1);
            break; // Found and removed, exit loop
          }
        }
      }
    }
    
    // Strategy 3: Final verification - check if still exists and remove
    const stillExists = state.data.All.some(contact => {
      if (!contact) return false;
      const cId = contact.contactId || contact.contactID;
      return cId && String(cId) === contactIdStr;
    });
    
    if (stillExists) {
      // Force remove using findIndex and splice
      let foundIndex = -1;
      for (let i = 0; i < state.data.All.length; i++) {
        const contact = state.data.All[i];
        if (contact) {
          const cId = contact.contactId || contact.contactID;
          if (cId && String(cId) === contactIdStr) {
            foundIndex = i;
            break;
          }
        }
      }
      if (foundIndex !== -1) {
        state.data.All.splice(foundIndex, 1);
      }
    }
  }
  
  // Remove from standard categories (EXCEPT Active - handled separately)
  const standardCategories = ["You", "Unassigned", "Assigned", "Closed","Blocked"];
  standardCategories.forEach(category => {
    if (state.data[category] && Array.isArray(state.data[category])) {
      state.data[category] = state.data[category].filter(
        contact => {
          if (!contact || !contact.contactId) return true; // Keep if no contactId
          // Compare as strings to handle number/string mismatches
          return String(contact.contactId) !== contactIdStr;
        }
      );
    }
  });

  // Handle Active array separately: only remove if activeStatus is 0 or undefined
  if (state.data.Active && Array.isArray(state.data.Active)) {
    if (activeStatus <= 0) {
      // Remove from Active if not active
      state.data.Active = state.data.Active.filter(
        contact => {
          if (!contact || !contact.contactId) return true;
          return String(contact.contactId) !== contactIdStr;
        }
      );
    }
    // If activeStatus > 0, keep in Active (don't remove)
  }

  // Remove from TeamMembers
  if (state.data.TeamMembers && Array.isArray(state.data.TeamMembers)) {
    state.data.TeamMembers.forEach(member => {
      if (Array.isArray(member.data)) {
        member.data = member.data.filter(contact => {
          if (!contact || !contact.contactId) return true;
          return String(contact.contactId) !== contactIdStr;
        });
        member.count = member.data.length;
      }
    });
  }

  // Remove from Teams
  if (state.data.Teams && Array.isArray(state.data.Teams)) {
    state.data.Teams.forEach(team => {
      if (Array.isArray(team.data)) {
        team.data = team.data.filter(contact => {
          if (!contact || !contact.contactId) return true;
          return String(contact.contactId) !== contactIdStr;
        });
        team.count = team.data.length;
      }
    });
  }
};

// Helper function to normalize ID for comparison (handles both string and ObjectId)
const normalizeId = (id) => {
  if (!id) return null;
  return String(id);
};

// Helper function to check if an ID belongs to a team, team member, or AI agent by
// checking global state
const determineAssignmentType = (assignedToId, assignedToName, assignedToIsAi) => {
  // Direct signal from the server (see socketHandler.js's assignedToIsAi forwarding) —
  // skips the aiAgents roster lookup below entirely, so this can never misclassify an
  // AI assignment as a "team member" just because the roster hasn't finished loading
  // yet on this client (a real race the roster-lookup fallback below is still exposed
  // to, kept only for payloads/paths that predate this flag).
  if (assignedToIsAi === true) return 'agent';

  // Try to access global store to check inboxSettings / aiAgents
  if (typeof window !== 'undefined' && window.store) {
    try {
      const globalState = window.store.getState();
      const inboxSettings = globalState?.inboxSettings;

      if (inboxSettings) {
        // Check teams first
        if (inboxSettings.teams && Array.isArray(inboxSettings.teams)) {
          const isTeam = inboxSettings.teams.some(team =>
            normalizeId(team._id) === normalizeId(assignedToId) ||
            normalizeId(team.id) === normalizeId(assignedToId)
          );
          if (isTeam) return 'team';
        }

        // Check team members
        if (inboxSettings.teamMembers && Array.isArray(inboxSettings.teamMembers)) {
          const isMember = inboxSettings.teamMembers.some(member =>
            normalizeId(member._id) === normalizeId(assignedToId) ||
            normalizeId(member.id) === normalizeId(assignedToId)
          );
          if (isMember) return 'member';
        }
      }

      // Check AI agents (aiAgentsSlice — see AI Takeover, aiTakeoverEventHandler.js).
      // An agent is neither a team nor a team member, so without this check it fell
      // through to the "default to team member" branch below and got mis-added to
      // state.data.TeamMembers as a fake team member (see updateTeamAssignment's caller).
      const aiAgents = globalState?.aiAgents?.agents;
      if (Array.isArray(aiAgents)) {
        const isAgent = aiAgents.some(agent =>
          normalizeId(agent._id) === normalizeId(assignedToId) ||
          normalizeId(agent.id) === normalizeId(assignedToId)
        );
        if (isAgent) return 'agent';
      }
    } catch (error) {
      // Silently fail and fall back to heuristics
    }
  }

  return null; // Default to member if can't determine
};

// Simple function to update team assignments
const updateTeamAssignment = (state, contactId, newAssignedTo, oldAssignedTo, safeContactData) => {
  // Initialize arrays if they don't exist
  if (!state.data.TeamMembers) state.data.TeamMembers = [];
  if (!state.data.Teams) state.data.Teams = [];

  // Normalize contactId for comparison
  const normalizedContactId = normalizeId(contactId);

  // Remove from old assignment
  if (oldAssignedTo && oldAssignedTo !== "Unassigned") {
    const normalizedOldId = normalizeId(oldAssignedTo);
    
    // Remove from TeamMembers - use string comparison
    const oldMember = state.data.TeamMembers.find(member => 
      normalizeId(member._id) === normalizedOldId || 
      normalizeId(member.id) === normalizedOldId
    );
    if (oldMember) {
      if (Array.isArray(oldMember.data)) {
        oldMember.data = oldMember.data.filter(contact => {
          const cId = contact?.contactId || contact?.contactID;
          return cId && normalizeId(cId) !== normalizedContactId;
        });
      } else {
        oldMember.data = [];
      }
      // Update count
      oldMember.count = oldMember.data.length;
    }

    // Remove from Teams - use string comparison
    const oldTeam = state.data.Teams.find(team => 
      normalizeId(team._id) === normalizedOldId || 
      normalizeId(team.id) === normalizedOldId
    );
    if (oldTeam) {
      if (Array.isArray(oldTeam.data)) {
        oldTeam.data = oldTeam.data.filter(contact => {
          const cId = contact?.contactId || contact?.contactID;
          return cId && normalizeId(cId) !== normalizedContactId;
        });
      } else {
        oldTeam.data = [];
      }
      // Update count
      oldTeam.count = oldTeam.data.length;
    }
  }

  // Add to new assignment
  if (newAssignedTo && newAssignedTo !== "Unassigned") {
    const normalizedNewId = normalizeId(newAssignedTo);
    const assignedToName = safeContactData?.assignedToName || safeContactData?.lastMessage?.assignedToName;
    
    // Check if it's a team member assignment - use string comparison
    const existingMember = state.data.TeamMembers.find(member => 
      normalizeId(member._id) === normalizedNewId || 
      normalizeId(member.id) === normalizedNewId
    );
    
    if (existingMember) {
      // Update existing team member
      const existingContactIndex = existingMember.data.findIndex(contact => {
        const cId = contact?.contactId || contact?.contactID;
        return cId && normalizeId(cId) === normalizedContactId;
      });
      if (existingContactIndex !== -1) {
        existingMember.data[existingContactIndex] = { ...existingMember.data[existingContactIndex], ...safeContactData };
      } else {
        existingMember.data.unshift(safeContactData);
      }
      existingMember.count = existingMember.data.length;
    } else {
      // Check if it's a team assignment - use string comparison
      const existingTeam = state.data.Teams.find(team => 
        normalizeId(team._id) === normalizedNewId || 
        normalizeId(team.id) === normalizedNewId
      );
      
      if (existingTeam) {
        // Update existing team
        const existingContactIndex = existingTeam.data.findIndex(contact => {
          const cId = contact?.contactId || contact?.contactID;
          return cId && normalizeId(cId) === normalizedContactId;
        });
        if (existingContactIndex !== -1) {
          existingTeam.data[existingContactIndex] = { ...existingTeam.data[existingContactIndex], ...safeContactData };
        } else {
          existingTeam.data.unshift(safeContactData);
        }
        existingTeam.count = existingTeam.data.length;
      } else {
        // Not found in either array - need to determine if it's a team, team member, or AI agent
        const assignmentType = determineAssignmentType(newAssignedTo, assignedToName, safeContactData?.assignedToIsAi);

        if (assignmentType === 'agent') {
          // AI Takeover assignee — not a team or team member, so it has no bucket
          // here to add into (state.data.TeamMembers/Teams is a "which human/team
          // is this contact in the queue for" sidebar list). The contact's own
          // assignedToIsAi/assignedToName already carry the AI info for card display
          // (see inboxController.js's aiTakeover read-time override) — nothing more to do.
        } else if (assignmentType === 'team') {
          // Create new team entry
          const newTeam = {
            _id: newAssignedTo,
            id: newAssignedTo,
            count: 1,
            data: [safeContactData],
            teamName: assignedToName || newAssignedTo
          };
          state.data.Teams.push(newTeam);
        } else {
          // Default to team member (or if assignmentType is 'member' or null)
          const newMember = {
            _id: newAssignedTo,
            id: newAssignedTo,
            count: 1,
            data: [safeContactData],
            name: assignedToName || newAssignedTo
          };
          state.data.TeamMembers.push(newMember);
        }
      }
    }
  }

  // Live-patch the AI Agents rail's per-agent cache too (agentByAgentId — see
  // fetchAgentTabInbox), the same "assign to AI" / "take back from AI" event this
  // whole function already reacts to for TeamMembers/Teams.
  if (state.agentByAgentId) {
    if (oldAssignedTo && oldAssignedTo !== "Unassigned") {
      const oldBucket = state.agentByAgentId[normalizeId(oldAssignedTo)];
      if (oldBucket && Array.isArray(oldBucket.data)) {
        oldBucket.data = oldBucket.data.filter(contact => {
          const cId = contact?.contactId || contact?.contactID;
          return cId && normalizeId(cId) !== normalizedContactId;
        });
        oldBucket.count = oldBucket.data.length;
      }
    }
    if (newAssignedTo && newAssignedTo !== "Unassigned") {
      // Only ever patches an agent tab that's already cached (its row was clicked at
      // least once this session) — never invents one. The sidebar's count BADGE does
      // not depend on this: it reads aiAgentsSlice's own counts (fetched upfront,
      // kept live via adjustAgentCount — see InboxSettings.jsx and
      // inboxAssistController.js's emitAssignmentCountChanged calls), which is
      // authoritative independent of whether this specific bucket is cached. This
      // block exists only to keep an ALREADY-OPEN agent tab's own contact list live.
      const newBucket = state.agentByAgentId[normalizeId(newAssignedTo)];
      if (newBucket && Array.isArray(newBucket.data)) {
        const existingIdx = newBucket.data.findIndex(contact => {
          const cId = contact?.contactId || contact?.contactID;
          return cId && normalizeId(cId) === normalizedContactId;
        });
        if (existingIdx !== -1) {
          newBucket.data[existingIdx] = { ...newBucket.data[existingIdx], ...safeContactData };
        } else {
          newBucket.data.unshift(safeContactData);
        }
        newBucket.count = newBucket.data.length;
      }
    }
  }
};